import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FilmIcon,
  InformationCircleIcon,
  LinkIcon,
  PlayCircleIcon,
  SparklesIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  TrashIcon,
  UserIcon,
  ViewfinderCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import ReactPlayer from "react-player";
import {
  Panel,
  Group as PanelGroup,
  Separator as PanelResizeHandle,
} from "react-resizable-panels";
import { useLocation } from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import { ReactComponent as Redo } from "../assets/icons/Redo.svg";
import { ReactComponent as EditMatchState } from "../assets/icons/Task-List-Edit.svg";
import { ReactComponent as TVCancel } from "../assets/icons/Tv-Disable.svg";
import { ReactComponent as TVRetro } from "../assets/icons/Tv-Retro.svg";
import { ReactComponent as Undo } from "../assets/icons/Undo.svg";
import CodingCourt from "../components/panels/CodingCourt";
import CurrentMatchState from "../components/panels/CurrentMatchState";
import EventEditor from "../components/panels/EventEditor";
import Scoreboard from "../components/panels/Scoreboard";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { saveMatch, updateMatchVideoFile } from "../utils/dbutils";
import { generateRandomSet } from "../utils/generateRandomSet";
import {
  kKeypoint,
  kRally,
  kRallyApproach,
  kRallyBounceOverhead,
  kRallyDrive,
  kRallyDriveVolley,
  kRallyDropshot,
  kRallyDropVolley,
  kRallyFlat,
  kRallyHalfVolley,
  kRallyKick,
  kRallyLob,
  kRallyOutcomeForcingError,
  kRallyOutcomeIn,
  kRallyOutcomeNetted,
  kRallyOutcomeOutPassingShot,
  kRallyOutcomePassingShot,
  kRallyOutcomePutAway,
  kRallyOutcomeUnforcedError,
  kRallyOutcomeWinner,
  kRallyOverhead,
  kRallyPassingShot,
  kRallyPowerShot,
  kRallyServeVolley,
  kRallySlice,
  kRallySmash,
  kRallyTopspin,
  kRallyVolley,
  kRallyVolleyPoach,
  kReturnForcingError,
  kReturnIn,
  kReturnNetted,
  kReturnUnforcedError,
  kReturnWinner,
  kServeAce,
  kServeFootfault,
  kServeIn,
  kServeLet,
  kServeNet,
  kServeOut,
  kServeSecond,
  kServeWinner,
  kServing,
  kStageFirstReturnAd,
  kStageFirstReturnDeuce,
  kStageFirstServeAd,
  kStageFirstServeDeuce,
  kStageRally,
  kStageSecondReturnAd,
  kStageSecondReturnDeuce,
  kStageSecondServeAd,
  kStageSecondServeDeuce,
} from "../utils/pstsDefines";
import { eventString, writeToPSTS } from "../utils/pstsFile";
import {
  playerInitialAndName,
  stringToPoint,
} from "../utils/utils";

// Google Drive "share" links (…/file/d/<id>/view) point to a web page, not a
// media file, so ReactPlayer can't play them. Convert them to a direct-download
// URL. The trailing "#video.mp4" is a client-side fragment (never sent to the
// server) that makes ReactPlayer recognise the extension-less URL as a video
// and render a <video> element.
function resolveVideoUrl(url) {
  if (!url || typeof url !== "string" || !url.includes("drive.google.com")) {
    return url;
  }
  const idMatch = url.match(/\/d\/([^/?#]+)/) || url.match(/[?&]id=([^&#]+)/);
  if (!idMatch) return url;
  return `https://drive.usercontent.google.com/download?id=${idMatch[1]}&export=download#video.mp4`;
}

// Assumed video frame rate — the browser doesn't expose the real one for a
// generic <video> element, so a single frame is approximated as 1/30s (the
// common frame rate for coded match footage). Used as the smallest seek
// increment for mouse-wheel scrubbing and the frame-step buttons.
const FRAME_SECONDS = 1 / 30;

// Event Editor keyboard shortcuts — user-remappable (see the Keyboard
// Shortcuts modal) and persisted to localStorage so remaps survive reloads.
// `category` distinguishes shortcuts that act on the video regardless of
// event selection ("video") from ones that act on the selected event ("event").
const SHORTCUT_ACTIONS = [
  { id: "togglePlayPause", label: "Toggle video play/pause", defaultKey: " ", category: "video" },
  { id: "advance1s", label: "Advance video 1 second", defaultKey: "x", category: "video" },
  { id: "rewind1s", label: "Rewind video 1 second", defaultKey: "z", category: "video" },
  { id: "toggleHand", label: "Toggle Backhand/Forehand", defaultKey: "h", category: "event" },
  { id: "winner", label: "Winner", defaultKey: "w", category: "event" },
  { id: "toggleError", label: "Toggle Unforced Error/Forced Error", defaultKey: "u", category: "event" },
  { id: "toggleSpin", label: "Toggle Slice/Topspin", defaultKey: "s", category: "event" },
  { id: "volley", label: "Volley", defaultKey: "v", category: "event" },
  { id: "passing", label: "Passing Winner (ball in) / Passing Out (ball out)", defaultKey: "p", category: "event" },
  { id: "lob", label: "Lob", defaultKey: "l", category: "event" },
  { id: "smash", label: "Smash", defaultKey: "m", category: "event" },
  { id: "drop", label: "Drop", defaultKey: "r", category: "event" },
  { id: "approach", label: "Approach", defaultKey: "a", category: "event" },
  { id: "overhead", label: "Overhead", defaultKey: "o", category: "event" },
  { id: "serveVolley", label: "Serve Volley", defaultKey: "y", category: "event" },
  { id: "power", label: "Power", defaultKey: "e", category: "event" },
  { id: "flat", label: "Flat", defaultKey: "f", category: "event" },
  { id: "kick", label: "Kick", defaultKey: "k", category: "event" },
  { id: "driveVolley", label: "Drive Volley", defaultKey: "i", category: "event" },
  { id: "cycleReach", label: "Cycle reach: Balanced → Unbalanced → Time Poor → N/A", defaultKey: "b", category: "event" },
];
const SHORTCUT_KEYS_STORAGE_KEY = "psts_coding_shortcut_keys";

function loadShortcutKeys() {
  const saved = JSON.parse(localStorage.getItem(SHORTCUT_KEYS_STORAGE_KEY) ?? "null") ?? {};
  const merged = {};
  for (const a of SHORTCUT_ACTIONS) merged[a.id] = saved[a.id] ?? a.defaultKey;
  return merged;
}

// " " (space) has no readable uppercase form of its own.
function formatShortcutKey(key) {
  return key === " " ? "Space" : key?.toUpperCase();
}

function CodingPage() {
  // const refRight = React.useRef(null);
  // const refRightTop = React.useRef(null);
  const location = useLocation();
  const handle = useFullScreenHandle();
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const { currentUser } = useAuthStatus();
  const { player1, player2, match } = location.state;
  const [matchData, setMatchData] = React.useState(null);
  const [currentEvent, setCurrentEvent] = React.useState(null);
  const [currentStage, setCurrentStage] = React.useState(kStageFirstServeDeuce);
  const [currentPlayer, setCurrentPlayer] = React.useState(player1);
  const [currentServer, setCurrentServer] = React.useState(player1);
  const [currentSet, setCurrentSet] = React.useState(
    match && match.sets && match.sets.length > 0
      ? match.sets[match.sets.length - 1]
      : null,
  );
  const [topPlayer, setTopPlayer] = React.useState(player1);
  const [bottomPlayer, setBottomPlayer] = React.useState(player2);
  const [currentSide, setCurrentSide] = React.useState(0);
  // Doubles serving/receiving rotation (see getTeammate/pickAutoServer/
  // getFixedReceiver/resolveRallyIndividual below) — player1/player2/
  // topPlayer/bottomPlayer/currentServer/currentPlayer continue to hold
  // TEAM-level objects unchanged (that machinery already works for doubles
  // as-is, since a team's guid is compared exactly like a player's guid
  // everywhere); these extra fields track which INDIVIDUAL teammate
  // serves/receives, per real doubles rules: partners strictly alternate
  // serving games all set (order only settable again next set), and each
  // team's deuce/ad receiver is fixed all set too. Not part of the
  // undo/redo snapshot system (see addToUndo) — undoing won't rewind
  // rotation state, only the score/event history.
  const [teamANextServer, setTeamANextServer] = useState(null);
  const [teamBNextServer, setTeamBNextServer] = useState(null);
  const [teamADeuceReceiver, setTeamADeuceReceiver] = useState(null);
  const [teamAAdReceiver, setTeamAAdReceiver] = useState(null);
  const [teamBDeuceReceiver, setTeamBDeuceReceiver] = useState(null);
  const [teamBAdReceiver, setTeamBAdReceiver] = useState(null);
  // The two individuals actually "in" the CURRENT point (whoever served and
  // whoever returned it) — every rally shot of that point is attributed to
  // one of these two, whichever team is hitting (see resolveRallyIndividual).
  const [pointServerIndividual, setPointServerIndividual] = useState(null);
  const [pointReceiverIndividual, setPointReceiverIndividual] = useState(null);
  // Blocks the whole coding UI at the start of every new doubles set to ask
  // just 2 things: who serves first (for whichever team serves game 1) and
  // who receives first (for the opposition team) — both teams' full
  // deuce/ad formation and serving order for the rest of the set follow
  // deterministically from these 2 picks (see the "Start Set" handler).
  const [needsDoublesSetup, setNeedsDoublesSetup] = useState(false);
  const [setupFirstServer, setSetupFirstServer] = useState(null);
  const [setupFirstReceiver, setSetupFirstReceiver] = useState(null);
  // Fallback only — { team, title, onPick(individualPlayer) } — used if
  // rotation state is somehow still unset when coding starts (e.g. resuming
  // a set coded before this feature existed) instead of silently
  // misattributing a shot.
  const [hitterPrompt, setHitterPrompt] = useState(null);
  const [events, setEvents] = React.useState(
    currentSet ? currentSet.events : [],
  );
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [scores, setScores] = React.useState({
    player1GameScore: 0,
    player2GameScore: 0,
    player1SetScore: 0,
    player2SetScore: 0,
  });
  const [rightPanelSize, setRightPanelSize] = React.useState({
    width: 0,
    height: 0,
  });
  const [pointNumber, setPointNumber] = React.useState(0);
  const [rightWidth, setRightWidth] = React.useState(0);
  const [isCourtVertical, setIsCourtVertical] = React.useState(true);
  const [isShowingVideo, setIsShowingVideo] = React.useState(false);
  const [isPortrait, setIsPortrait] = React.useState(true);
  const [, forceUpdate] = React.useState(false);
  const eventTypes = ["Serve", "Return", "Rally", "Keypoint"];
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const videoRef = React.useRef(null);
  const playerRef = React.useRef(null);
  // Ensures the persisted video position (see match.videoTime) is only
  // auto-seeked-to once per mount, not on every onReady re-fire (e.g. after
  // switching the video URL/set).
  const hasRestoredVideoPositionRef = React.useRef(false);
  // Video position at the moment edit mode was entered — restored by Resume
  // Coding instead of always jumping to the current event's recorded time.
  const preEditVideoTimeRef = React.useRef(null);
  const courtRef = React.useRef(null);
  const videoContainerRef = React.useRef(null);
  const eventsEndRef = React.useRef(null);
  const videoAreaRef = React.useRef(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  // Progress-bar state: current playback position and total duration. Updated
  // from ReactPlayer's onProgress/onDuration callbacks; `isSeekBarDragging`
  // suppresses onProgress updates while the user is actively dragging the bar
  // so it doesn't jump back to the (stale) playback position mid-drag.
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isSeekBarDragging, setIsSeekBarDragging] = useState(false);
  // Start muted so iOS/iPadOS will render the video without a user gesture
  // (muted autoplay is permitted; unmuted is blocked, leaving a blank frame).
  const [isMuted, setIsMuted] = useState(true);
  const [videoFilePath, setVideoFilePath] = useState(() =>
    match?.videoFile && !match.videoFile.startsWith("blob:")
      ? match.videoFile
      : "https://youtu.be/d4E-qfL8LbI?si=H3vLG4PSfInEpQH7",
  );
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState("");
  const [showMatchStateModal, setShowMatchStateModal] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [shortcutKeys, setShortcutKeys] = useState(loadShortcutKeys);
  const [remapListeningId, setRemapListeningId] = useState(null);
  const [sizeMain, setSizeMain] = useState(null);
  const [sizeVideo, setSizeVideo] = useState({ width: 0, height: 0 });
  const [sizeInfo, setSizeInfo] = useState({ width: 0, height: 0 });
  const [sizeScoreboard, setSizeScoreboard] = useState({ width: 0, height: 0 });
  const [sizeEventsList, setSizeEventsList] = useState({ width: 0, height: 0 });
  const [sizeEventEditor, setSizeEventEditor] = useState({
    width: 0,
    height: 0,
  });
  const [sizeCourt, setSizeCourt] = useState({ width: 0, height: 0 });
  const [pcVideo, setPcVideo] = useState({ width: 0, height: 0 });
  const [pcInfo, setPcInfo] = useState({ width: 0, height: 0 });
  const [pcScoreboard, setPcScoreboard] = useState({ width: 0, height: 0 });
  const [pcEventsList, setPcEventsList] = useState({ width: 0, height: 0 });
  const [pcEventEditor, setPcEventEditor] = useState({
    width: 0,
    height: 0,
  });
  const [pcCourt, setPcCourt] = useState({ width: 0, height: 0 });
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  // Rally deletion: chronological index of the oldest rally selected for
  // deletion (that rally and every newer one are removed). null = none selected.
  const [deleteFromIdx, setDeleteFromIdx] = useState(null);
  // Edit mode: the court is showing/editable for a past shot selected from the
  // events list, rather than accepting new live-coded shots. Entered by
  // clicking any event in the list; left via the "Resume Coding" button.
  const [isEditMode, setIsEditMode] = useState(false);
  // Queued re-code of the current event after undoing it because its redrawn
  // path changed the result (see onUpdateEvent) — processed by the effect
  // below once the undo's state updates have actually applied.
  const [pendingRedraw, setPendingRedraw] = useState(null);
  const pixelWidthInfo = 180;

  const videoonsvg = "";
  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 50;

  const doMatchScores = (m) => {
    let scores = "";
    for (var mset of m.sets) {
      if (!mset.scores) continue;
      if (scores.length > 0) {
        scores += ", ";
      }
      scores += `${mset.scores.player1SetScore}-${mset.scores.player2SetScore}`;
    }
    return scores;
  };

  const constructMatchData = async (m) => {
    // const secs = m.date.getTime() / 1000;
    const dt = m.date
      ? m.date
      : m.matchDate
        ? new Date(m.matchDate)
        : new Date();
    const md = {
      isDouble: false,
      username: m.username
        ? m.username
        : currentUser?.email
          ? currentUser?.email
          : "",
      data: "",
      date: dt,
      description: `${m.player1.lastName} vs ${m.player2.lastName} (${
        m.tournament?.name ?? ""
      })`, //"Djokovic vs Sinner (Australian Open 2024 2024)",
      guid: m.guid, // "8C7C16BE-2CD3-4FCA-882B-296AE469FCBC-189-000000A37CD93485_C25B05A9-3CF8-45C1-B98F-0703A6B311B8-204-0000034DC8CBFE37_1706276808453",
      player1CountryCode: m.player1.country?.code ?? "UNK", // "UNK",
      player1FirstName: m.player1.firstName, //"Novak",
      player1Guid: m.player1.guid, //"8C7C16BE-2CD3-4FCA-882B-296AE469FCBC-189-000000A37CD93485",
      player1LastName: m.player1.lastName, //"Djokovic",
      player2CountryCode: m.player2.country?.code ?? "UNK", //"UNK",
      player2FirstName: m.player2.firstName, //"Jannik",
      player2Guid: m.player2.guid, //"C25B05A9-3CF8-45C1-B98F-0703A6B311B8-204-0000034DC8CBFE37",
      player2LastName: m.player2.lastName, //"Sinner",
      roundName: m.round?.notes ?? m.roundName ?? "", //"Semi Finals",
      scores: doMatchScores(m), //"1-6, 2-6, 7-6, 3-6",
      seconds: m.date?.seconds ?? dt.getTime() / 1000,
      tournamentName: m.tournament?.name ?? "", //"Australian Open 2024",
      uid: m.uid ? m.uid : "", //"8C7C16BE-2CD3-4FCA-882B-296AE469FCBC-189-000000A37CD93485_C25B05A9-3CF8-45C1-B98F-0703A6B311B8-204-0000034DC8CBFE37_1706276808453",
      videoFilePath: "", //"https://www.youtube.com/watch?v=rHPYSHltYpg",
      videoStartTime: 0, //1706291217.356296,
    };
    const s = writeToPSTS(m);
    md.data = s;
    if (currentSet) {
      md.currentSet = currentSet;
      if (currentSet.currentState) {
        md.currentState = currentSet?.currentState;
      }
    }
    // Save the full original match (preserves matchDate, tournament, round, etc.)
    // rather than the stripped-down md object, to avoid overwriting those fields.
    const matchToSave = {
      ...m,
      data: md.data,
      scores: md.scores,
      description: md.description,
    };
    if (md.currentSet !== undefined) matchToSave.currentSet = md.currentSet;
    if (md.currentState !== undefined)
      matchToSave.currentState = md.currentState;
    const um = await saveMatch(matchToSave);
    m.uid = um.match?.uid ?? m.uid;
    return md;
  };

  const onTouchStart = (e) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    // if (isLeftSwipe || isRightSwipe) {
    //   console.log("swipe", isLeftSwipe ? "left" : "right");
    //   toast.warning("Swipe detected");
    // }
    // add your conditional logic here
  };

  const onDeleteEvent = (event) => {};

  // Deep-clone a state snapshot. Undo/redo stack entries — and anything
  // assigned to the "live" currentSet.currentState/scores — must always be
  // fully independent objects. Sharing nested objects (scores, events, …)
  // between a stack entry and the live state is what let later in-place
  // mutations silently rewrite earlier history, which is why Undo could
  // restore scores that no longer matched what actually happened at that
  // point in time.
  const cloneState = (st) => JSON.parse(JSON.stringify(st));

  const addToUndo = (explicitState) => {
    const st = cloneState(explicitState ?? currentSet.currentState);
    setUndoStack((prev) => [...prev, st]);
    setRedoStack([]);
    localStorage.setItem("currentmatch", JSON.stringify(match));
  };

  // Apply a (deep-cloned) state snapshot to every piece of live state — shared
  // by onUndo/onRedo so both restore exactly the same fields the same way.
  const applyRestoredState = (raw) => {
    const st = cloneState(raw);
    currentSet.currentState = st;
    currentSet.scores = st.scores;
    currentSet.events = st.events ?? [];
    setScores(st.scores);
    setCurrentPlayer(st.currentPlayer);
    setCurrentServer(st.currentServer);
    setCurrentStage(st.currentStage);
    setCurrentSide(st.currentSide);
    setTopPlayer(st.topPlayer);
    setBottomPlayer(st.bottomPlayer);
    setPointNumber(st.pointNumber ?? 0);
    setCurrentEvent(st.event);
    setSelectedEvent(st.event);
    setEvents(st.events ?? []);
    // Restore the video to whatever position it was at when this state was
    // recorded, if the event carries one — keeps the video in sync with the
    // scoreboard/court while stepping back and forth through history.
    const vt = st.event?.videoTime;
    if (vt != null && playerRef?.current) {
      playerRef.current.seekTo(vt, "seconds");
    }
    localStorage.setItem("currentmatch", JSON.stringify(match));
    // Persist to the database too — not just localStorage — so an undo/redo
    // that isn't followed by any further coding (e.g. the user navigates away
    // right after undoing) isn't silently lost, leaving a stale server/stage
    // to be restored the next time this match is opened.
    constructMatchData(match);
    return st;
  };

  const onUndo = () => {
    // The oldest entry is the set's baseline (ready-to-serve) state — never
    // pop past it, there is nothing before it to restore.
    if (undoStack.length <= 1) return;
    const uds = undoStack.slice(0, -1);
    const popped = undoStack[undoStack.length - 1];
    setRedoStack((prev) => [...prev, cloneState(popped)]);
    setUndoStack(uds);
    const st = uds[uds.length - 1];
    if (st) applyRestoredState(st);
  };

  const onRedo = () => {
    if (redoStack.length === 0) return;
    const rds = redoStack.slice(0, -1);
    const popped = redoStack[redoStack.length - 1];
    setRedoStack(rds);
    const restored = applyRestoredState(popped);
    setUndoStack((prev) => [...prev, cloneState(restored)]);
  };

  const onEditMatchState = () => {
    setShowMatchStateModal(true);
  };

  const onGenerateRandomSet = () => {
    const { events: newEvents, finalState } = generateRandomSet({
      player1,
      player2,
      currentServer,
      topPlayer,
      bottomPlayer,
      scores,
    });

    // Tennis game-score notation helper
    const tennisScore = (p, o) => {
      const labels = ["00", "15", "30", "40"];
      if (p < 4) return labels[p] ?? String(p);
      return p > o ? "A" : "40";
    };

    const setNum = currentSet?.setNumber ?? 1;
    newEvents.forEach((ev) => {
      ev.setNumber = setNum;
      ev.eventString = eventString(ev);
      const sc = ev.scores;
      if (sc && typeof sc === "object") {
        const {
          player1GameScore: p1g,
          player2GameScore: p2g,
          player1SetScore: p1s,
          player2SetScore: p2s,
        } = sc;
        ev.realScores = `${p1s}:${p2s} ${tennisScore(p1g, p2g)}:${tennisScore(p2g, p1g)}`;
      }
    });

    const allEvents = [...events, ...newEvents];
    currentSet.events = allEvents;
    currentSet.currentState = finalState;
    currentSet.scores = finalState.scores;

    setEvents(allEvents);
    setScores(finalState.scores);
    setCurrentPlayer(finalState.currentPlayer);
    setCurrentServer(finalState.currentServer);
    setCurrentStage(finalState.currentStage);
    setCurrentSide(finalState.currentSide);
    setTopPlayer(finalState.topPlayer);
    setBottomPlayer(finalState.bottomPlayer);
    setPointNumber(finalState.pointNumber);
    setSelectedEvent(allEvents[allEvents.length - 1] || null);
    setCurrentEvent(allEvents[allEvents.length - 1] || null);
    setUndoStack((prev) => [...prev, JSON.parse(JSON.stringify(finalState))]);
    setRedoStack([]);

    localStorage.setItem("currentmatch", JSON.stringify(match));
    constructMatchData(match).then((md) => setMatchData(md));
  };

  const handleVideoSelected = (event) => {
    const vfp = URL.createObjectURL(event.target.files[0]);
    setVideoFilePath(vfp);
    forceUpdate((n) => !n);
  };

  // Set the video URL and persist it on the match so it's restored next session
  // (blob: URLs are session-only object URLs, so they're not persisted).
  const applyVideoUrl = (url) => {
    setVideoFilePath(url);
    if (match && url && !url.startsWith("blob:")) {
      match.videoFile = url;
      try {
        localStorage.setItem("currentmatch", JSON.stringify(match));
      } catch (e) {
        /* ignore quota errors */
      }
      const uid = match.uid || currentUser?.uid;
      if (uid && match.guid) updateMatchVideoFile(uid, match.guid, url);
    }
  };

  // Doubles serving/receiving rules (ported from the iOS app's CourtView.m):
  // stage names already encode serve/return/deuce-ad, so no extra state is
  // needed to classify them.
  const isServeStageValue = (stg) =>
    stg === kStageFirstServeDeuce ||
    stg === kStageFirstServeAd ||
    stg === kStageSecondServeDeuce ||
    stg === kStageSecondServeAd;
  const isReturnStageValue = (stg) =>
    stg === kStageFirstReturnDeuce ||
    stg === kStageFirstReturnAd ||
    stg === kStageSecondReturnDeuce ||
    stg === kStageSecondReturnAd;
  const isDeuceStageValue = (stg) =>
    stg === kStageFirstServeDeuce ||
    stg === kStageSecondServeDeuce ||
    stg === kStageFirstReturnDeuce ||
    stg === kStageSecondReturnDeuce;

  // The OTHER player on `team` (the one who isn't `indiv`).
  const getTeammate = (team, indiv) => {
    if (!team || !indiv) return null;
    return team.player1?.guid === indiv.guid ? team.player2 : team.player1;
  };

  // Auto-advance a team's server rotation — used for every service game
  // within a set after the set's first (which is confirmed via the "Who
  // serves?" prompt instead, since real teams can change who serves first
  // each set).
  const pickAutoServer = (teamObj) => {
    const isTeamA = teamObj?.guid === match.teamA?.guid;
    const current = isTeamA ? teamANextServer : teamBNextServer;
    const mate = getTeammate(teamObj, current);
    if (isTeamA) setTeamANextServer(mate);
    else setTeamBNextServer(mate);
    return current;
  };

  // `teamObj`'s fixed deuce/ad receiver this set, or null if that team
  // hasn't received yet (the "Who receives?" prompt establishes it once,
  // then it holds for every remaining point of the set).
  const getFixedReceiver = (teamObj, isDeuceCourt) => {
    const isTeamA = teamObj?.guid === match.teamA?.guid;
    const deuce = isTeamA ? teamADeuceReceiver : teamBDeuceReceiver;
    const ad = isTeamA ? teamAAdReceiver : teamBAdReceiver;
    if (!deuce && !ad) return null;
    return isDeuceCourt ? deuce : ad;
  };

  // Fix `teamObj`'s deuce/ad receiver assignment for the rest of the set
  // from whichever teammate the coder picked to receive this point.
  const establishReceiver = (teamObj, indiv, isDeuceCourt) => {
    const isTeamA = teamObj?.guid === match.teamA?.guid;
    const mate = getTeammate(teamObj, indiv);
    if (isTeamA) {
      setTeamADeuceReceiver(isDeuceCourt ? indiv : mate);
      setTeamAAdReceiver(isDeuceCourt ? mate : indiv);
    } else {
      setTeamBDeuceReceiver(isDeuceCourt ? indiv : mate);
      setTeamBAdReceiver(isDeuceCourt ? mate : indiv);
    }
  };

  // Which team (teamA/teamB) `indiv` belongs to.
  const teamOfIndividual = (indiv) => {
    if (!indiv) return null;
    if (match.teamA?.player1?.guid === indiv.guid || match.teamA?.player2?.guid === indiv.guid) {
      return match.teamA;
    }
    if (match.teamB?.player1?.guid === indiv.guid || match.teamB?.player2?.guid === indiv.guid) {
      return match.teamB;
    }
    return null;
  };

  // A rally shot is attributed to whichever of THIS POINT's server/receiver
  // belongs to the team currently hitting (currentPlayer) — mirrors
  // CourtView.m's `hitter = (hittingTeam == servingTeam) ? server : receiver`.
  // Partners aren't individually tracked mid-rally beyond that.
  const resolveRallyIndividual = () => {
    if (!pointServerIndividual || !pointReceiverIndividual) return null;
    const serverTeam = teamOfIndividual(pointServerIndividual);
    return currentPlayer?.guid === serverTeam?.guid
      ? pointServerIndividual
      : pointReceiverIndividual;
  };

  // Rebuild the doubles rotation state (server/receiver formation + whose
  // turn it is to serve next) from a set's ALREADY-CODED events, instead of
  // asking again on resume — server/receiver identity is only ever chosen
  // once, at the set's first point (see the "Doubles set up" modal), and
  // every game after that follows deterministically. Returns true if it
  // found enough to reconstruct from (a set with no events yet still needs
  // the modal). `setEvents` must be this ONE set's events (rotation resets
  // every set).
  const reconstructDoublesRotation = (setEvents) => {
    const serveEvents = (setEvents ?? []).filter((e) => e.skill === 0 && e.individualPlayer);
    const returnEvents = (setEvents ?? []).filter((e) => e.skill === 1 && e.individualPlayer);
    const firstServer = serveEvents[0]?.individualPlayer;
    const firstReceiver = returnEvents[0]?.individualPlayer;
    const servingTeam = teamOfIndividual(firstServer);
    if (!firstServer || !firstReceiver || !servingTeam) return false;
    const servingTeamIsA = servingTeam.guid === match.teamA?.guid;
    const teamADeuce = servingTeamIsA ? firstServer : firstReceiver;
    const teamBDeuce = servingTeamIsA ? firstReceiver : firstServer;

    setTeamADeuceReceiver(teamADeuce);
    setTeamAAdReceiver(getTeammate(match.teamA, teamADeuce));
    setTeamBDeuceReceiver(teamBDeuce);
    setTeamBAdReceiver(getTeammate(match.teamB, teamBDeuce));

    // A team's next server is the teammate of whoever served their most
    // recent game, or (if they haven't served yet this set) their deuce
    // player — exactly what the "Doubles set up" modal would've set.
    const lastServerFor = (team) => {
      for (let i = serveEvents.length - 1; i >= 0; i--) {
        const indiv = serveEvents[i].individualPlayer;
        if (teamOfIndividual(indiv)?.guid === team.guid) return indiv;
      }
      return null;
    };
    const lastA = lastServerFor(match.teamA);
    const lastB = lastServerFor(match.teamB);
    setTeamANextServer(lastA ? getTeammate(match.teamA, lastA) : teamADeuce);
    setTeamBNextServer(lastB ? getTeammate(match.teamB, lastB) : teamBDeuce);

    // Also resume the CURRENT point's server/receiver (needed if the last
    // coded event was mid-rally) — harmless if stale, since the next serve
    // created will overwrite these anyway.
    const lastServe = serveEvents[serveEvents.length - 1];
    const lastReturn = returnEvents[returnEvents.length - 1];
    if (lastServe) setPointServerIndividual(lastServe.individualPlayer);
    if (lastReturn) setPointReceiverIndividual(lastReturn.individualPlayer);
    return true;
  };

  const processEvent = (event, preserve) => {
    let st = { ...currentSet.currentState };
    var skill = 2;
    var subskill1 = 0;
    var subskill2 = null;
    var outcome = null;
    var hand = 0;
    if (currentStage === kStageFirstServeDeuce) {
      skill = 0;
      subskill1 = 0;
      outcome = event.result === 0 ? 0 : 5;
      const cstage =
        event.result === 0 ? kStageFirstReturnDeuce : kStageSecondServeDeuce;
      setCurrentStage(cstage);
      st.currentStage = cstage;
    } else if (currentStage === kStageFirstServeAd) {
      skill = 0;
      subskill1 = 0;
      outcome = event.result === 0 ? 0 : 5;
      const cstage =
        event.result === 0 ? kStageFirstReturnAd : kStageSecondServeAd;
      setCurrentStage(
        cstage,
        // event.result === 0 ? kStageFirstReturnAd : kStageSecondServeAd
      );
      st.currentStage = cstage;
    } else if (currentStage === kStageSecondServeDeuce) {
      skill = 0;
      subskill1 = 1;
      outcome = event.result === 0 ? 0 : 5;
      const cstage =
        event.result === 0 ? kStageSecondReturnDeuce : kStageFirstServeAd;
      setCurrentStage(
        cstage,
        // event.result === 0 ? kStageSecondReturnDeuce : kStageFirstServeAd
      );
      st.currentStage = cstage;
    } else if (currentStage === kStageSecondServeAd) {
      skill = 0;
      subskill1 = 1;
      outcome = event.result === 0 ? 0 : 5;
      const cstage =
        event.result === 0 ? kStageSecondReturnAd : kStageFirstServeDeuce;
      setCurrentStage(
        cstage,
        // event.result === 0 ? kStageSecondReturnAd : kStageFirstServeDeuce
      );
      st.currentStage = cstage;
    } else if (
      currentStage === kStageFirstReturnDeuce ||
      currentStage === kStageFirstReturnAd ||
      currentStage === kStageSecondReturnDeuce ||
      currentStage === kStageSecondReturnAd
    ) {
      skill = 1;
      subskill2 = 2; // topspin
      // A return that isn't "in" is an error, not a winner — a winner is marked
      // manually by the user. Default an out return to Unforced Error.
      outcome = event.result === 0 ? kReturnIn : kReturnUnforcedError;
      const cstage =
        event.result === 0
          ? kStageRally
          : pointNumber % 2 !== 0
            ? kStageFirstServeDeuce
            : kStageFirstServeAd;
      setCurrentStage(
        cstage,
        // event.result === 0
        //   ? kStageRally
        //   : pointNumber % 2 !== 0
        //   ? kStageFirstServeDeuce
        //   : kStageFirstServeAd
      );
      st.currentStage = cstage;
    } else if (currentStage === kStageRally) {
      // A rally shot that isn't "in" is an error, not a winner — a winner is
      // marked manually by the user. Default an out shot to Unforced Error.
      // Either way it's now a decisive (point-ending) shot, i.e. a Keypoint,
      // not a plain (still in play) Rally shot.
      skill = event.result === 0 ? kRally : kKeypoint;
      subskill2 = 1; // topspin
      outcome = event.result === 0 ? kRallyOutcomeIn : kRallyOutcomeUnforcedError;
      const cstage =
        event.result === 0
          ? kStageRally
          : pointNumber % 2 !== 0
            ? kStageFirstServeDeuce
            : kStageFirstServeAd;
      setCurrentStage(cstage);
      st.currentStage = cstage;
    }
    event.skill = skill;
    // When re-coding an edited event (result changed), keep its original
    // hand/subskill1/subskill2 instead of the freshly-computed defaults —
    // only the result/outcome (derived above from the new position) and the
    // stage transition should change.
    event.subskill1 = preserve?.subskill1 ?? subskill1;
    event.subskill2 = preserve?.subskill2 ?? subskill2;
    event.outcome = outcome;
    event.hand = preserve?.hand ?? hand;
    event.player = currentPlayer;
    event.videoTime = playerRef?.current
      ? playerRef?.current.getCurrentTime()
      : 0;
    // event.timestamp = new Date();
    return st;
  };

  // Doubles: resolve which individual teammate hit each shot before actually
  // creating the event — server/receiver rotation is fully auto (see
  // pickAutoServer/getFixedReceiver) since real teams only choose their
  // serving order and deuce/ad receivers once, upfront, per set (see the
  // "Doubles set up" modal triggered from createNewSet). Rally shots are
  // attributed to whichever of the point's server/receiver is on the
  // currently-hitting team (resolveRallyIndividual), matching CourtView.m.
  // Singles bypasses all of this immediately.
  const onCreateEvent = (event, preserve) => {
    if (match.isDoubles) {
      if (isServeStageValue(currentStage)) {
        const servingTeam = currentPlayer;
        // The server only rotates to the next teammate on the very first
        // serve of a new GAME (pointNumber 0) — kStageFirstServeDeuce also
        // recurs later in the same game (e.g. 15-15, 30-30), and a fault's
        // second serve is still the same point, so both must reuse whoever
        // already served this game instead of re-rotating.
        const isFirstServeAttempt =
          currentStage === kStageFirstServeDeuce ||
          currentStage === kStageFirstServeAd;
        const isFirstPointOfGame = pointNumber === 0;
        const indiv =
          isFirstPointOfGame && isFirstServeAttempt
            ? pickAutoServer(servingTeam)
            : pointServerIndividual;
        // Fallback only: rotation wasn't set up (e.g. resuming a set coded
        // before this feature existed) — ask instead of misattributing.
        if (!indiv) {
          const isTeamA = servingTeam?.guid === match.teamA?.guid;
          setHitterPrompt({
            team: isTeamA ? match.teamA : match.teamB,
            title: "Who serves?",
            onPick: (picked) => {
              setPointServerIndividual(picked);
              setHitterPrompt(null);
              finaliseCreateEvent(event, preserve, picked);
            },
          });
          return;
        }
        setPointServerIndividual(indiv);
        finaliseCreateEvent(event, preserve, indiv);
        return;
      }
      if (isReturnStageValue(currentStage)) {
        const receivingTeam = currentPlayer;
        const isDeuce = isDeuceStageValue(currentStage);
        const indiv = getFixedReceiver(receivingTeam, isDeuce);
        if (!indiv) {
          const isTeamA = receivingTeam?.guid === match.teamA?.guid;
          setHitterPrompt({
            team: isTeamA ? match.teamA : match.teamB,
            title: "Who receives?",
            onPick: (picked) => {
              establishReceiver(receivingTeam, picked, isDeuce);
              setPointReceiverIndividual(picked);
              setHitterPrompt(null);
              finaliseCreateEvent(event, preserve, picked);
            },
          });
          return;
        }
        setPointReceiverIndividual(indiv);
        finaliseCreateEvent(event, preserve, indiv);
        return;
      }
      if (currentStage === kStageRally) {
        finaliseCreateEvent(event, preserve, resolveRallyIndividual());
        return;
      }
    }
    finaliseCreateEvent(event, preserve, null);
  };

  const finaliseCreateEvent = (event, preserve, individualPlayer) => {
    let isEndOfPoint = false;
    let pest = processEvent(event, preserve);

    event.player = currentPlayer;
    if (match.isDoubles && individualPlayer) {
      event.individualPlayer = individualPlayer;
      event.individualPlayerGuid = individualPlayer.guid;
    }
    const ts = playerRef?.current
      ? playerRef.current.getCurrentTime()
      : new Date().getTime() / 1000;
    event.timestampseconds = ts;
    if (playerRef?.current) {
      event.videoTime = playerRef.current.getCurrentTime();
    }
    event.scores = { ...scores };
    event.reach = preserve?.reach ?? 0;
    if (match.isDoubles) {
      if (event.skill === 0) {
        event.serveFormation = preserve?.serveFormation ?? 0;
        event.netPosition = preserve?.netPosition ?? 0;
      } else if (event.skill === 1) {
        event.returnFormation = preserve?.returnFormation ?? 0;
      }
    }
    if (!preserve && event.skill !== 0) {
      event.subskill2 = kRallyTopspin;
    }

    setCurrentEvent(event);
    const evs = events.slice();
    evs.push(event);
    setEvents(evs);
    setSelectedEvent(event);
    // Keep the "live" set object in sync immediately — endOfPoint (called
    // below) reads currentSet.events synchronously and must already see this
    // event, or the point-ending snapshot recorded for undo/resume would be
    // missing the very event that ended the point.
    currentSet.events = evs;

    let cstage = pest.currentStage;
    let cplayer = currentPlayer;
    let cserver = currentServer;
    let cside = currentSide;
    let ctop = topPlayer;
    let cbottom = bottomPlayer;
    let cpoint = pointNumber;

    // CodingCourt only ever reports the raw ball-position tap here: 0 = landed
    // in bounds, -1 = landed out. Translate that into this event's actual
    // (legacy-format) stored result — 0 = error, 1 = in play/undecided, 3 =
    // winner — before it's kept anywhere; a winner/ace is only ever set later,
    // once the coder explicitly picks that outcome (see doOutcomeChanged).
    const landedIn = event.result === 0;
    event.result = landedIn ? 1 : 0;

    if (!landedIn) {
      if (currentStage === kStageFirstServeDeuce) {
        cstage = kStageSecondServeDeuce;
        setCurrentStage(kStageSecondServeDeuce);
      } else if (currentStage === kStageFirstServeAd) {
        cstage = kStageSecondServeAd;
        setCurrentStage(kStageSecondServeAd);
      } else {
        isEndOfPoint = true;
        // endOfPoint fully manages currentSet.currentState and records the
        // resulting state on the undo stack exactly once — nothing further
        // to do here for this branch.
        endOfPoint(event);
      }
    } else {
      cplayer = currentPlayer.guid === player1.guid ? player2 : player1;
      setCurrentPlayer(cplayer);
      cside = currentSide === 0 ? 1 : 0;
      setCurrentSide(cside);
    }

    if (!isEndOfPoint) {
      currentSet.currentState = {
        scores: { ...currentSet.scores },
        currentPlayer: cplayer,
        currentServer: cserver,
        currentStage: cstage,
        currentSide: cside,
        topPlayer: ctop,
        bottomPlayer: cbottom,
        pointNumber: cpoint,
        event: { ...event },
        events: evs,
      };
      addToUndo();
    }

    // A shot that finished near the net is auto-classified (the coder can still
    // change it in the event editor):
    //   serve  → NET (server's side, a fault) | LET (crossed over, replay)
    //   rally  → NETTED (never crossed the net)
    if (event.netHint === "let") {
      event.result = 1;
      event.outcome = kServeLet;
      snapServeToNet(event);
      doLetServe(event);
    } else if (event.netHint === "net") {
      event.outcome = kServeNet;
      snapServeToNet(event);
      forceUpdate((n) => !n);
    } else if (event.netHint === "netted") {
      event.outcome =
        event.skill === 1 ? kReturnNetted : kRallyOutcomeNetted;
      snapServeToNet(event);
      forceUpdate((n) => !n);
    }
  };

  const doPlayerChanged = (player) => {
    if (match.isDoubles) {
      selectedEvent.individualPlayer = player;
      selectedEvent.individualPlayerGuid = player.guid;
      const team = teamOfIndividual(player);
      if (team) selectedEvent.player = team;
      forceUpdate((n) => !n);
      return;
    }
    selectedEvent.player = player;
    forceUpdate((n) => !n);
  };

  const doHandChanged = (hand) => {
    selectedEvent.hand = hand;
    forceUpdate((n) => !n);
  };

  const doServeChanged = (serve) => {
    selectedEvent.subskill1 = serve;
  };

  const doReachChanged = (reach) => {
    selectedEvent.reach = reach;
    forceUpdate((n) => !n);
  };

  const doServeFormationChanged = (serveFormation) => {
    selectedEvent.serveFormation = serveFormation;
    forceUpdate((n) => !n);
  };

  const doNetPositionChanged = (netPosition) => {
    selectedEvent.netPosition = netPosition;
    forceUpdate((n) => !n);
  };

  const doReturnFormationChanged = (returnFormation) => {
    selectedEvent.returnFormation = returnFormation;
    forceUpdate((n) => !n);
  };

  const doSubskill2Changed = (subskill2) => {
    const returnss2 = [
      kRallyDrive,
      kRallySlice,
      kRallyTopspin,
      kRallyApproach,
      kRallyLob,
      kRallyDropshot,
      kRallyPowerShot,
      kRallyFlat,
      kRallyKick,
    ];
    const rallyss2 = [
      kRallySlice,
      kRallyTopspin,
      kRallyVolley,
      kRallyOverhead,
      kRallySmash,
      kRallyLob,
      kRallyDropshot,
      kRallyDriveVolley,
      kRallyHalfVolley,
      kRallyServeVolley,
      kRallyApproach,
      kRallyPassingShot,
      kRallyPowerShot,
      kRallyFlat,
      kRallyKick,
      kRallyDropVolley,
      kRallyVolleyPoach,
      kRallyBounceOverhead,
    ];
    if (selectedEvent.skill === 1) {
      selectedEvent.subskill2 = returnss2[subskill2];
    } else if (selectedEvent.skill === 2 || selectedEvent.skill === 3) {
      selectedEvent.subskill2 = rallyss2[subskill2];
    }
    forceUpdate((n) => !n);
  };

  // Right-click shortcuts (see CodingCourt) — apply to the most recently
  // coded event, and only for a return/rally shot (a serve has no hand/spin
  // toggle in the same sense).
  const toggleCurrentEventHand = () => {
    if (!currentEvent || currentEvent.skill === kServing) return;
    currentEvent.hand = currentEvent.hand === 0 ? 1 : 0;
    forceUpdate((n) => !n);
  };

  const toggleCurrentEventSpin = () => {
    if (!currentEvent || currentEvent.skill === kServing) return;
    currentEvent.subskill2 =
      currentEvent.subskill2 === kRallyTopspin ? kRallySlice : kRallyTopspin;
    forceUpdate((n) => !n);
  };

  const doFootFault = (event) => {
    for (let i = undoStack.length - 1; i > 0; i--) {
      const st = undoStack[i];
      if (st.event.result === 1 && st.event.skill === kServing) {
        // Discard any "future" entries beyond this point — we're branching
        // the history here (re-recording this serve as a foot fault), so
        // anything recorded after it no longer applies.
        setUndoStack(undoStack.slice(0, i + 1));
        setRedoStack([]);

        event.result = 0;
        event.outcome = kServeFootfault;
        const restored = cloneState(st);
        setCurrentPlayer(restored.currentPlayer);
        setCurrentServer(restored.currentServer);
        setCurrentSide(restored.currentSide);
        setTopPlayer(restored.topPlayer);
        setBottomPlayer(restored.bottomPlayer);
        setScores(restored.scores);
        setCurrentStage(restored.currentStage);
        setPointNumber(restored.pointNumber);
        currentSet.currentState = restored;
        currentSet.currentState.event = { ...event };
        currentSet.scores = restored.scores;
        if (
          currentSet.currentState.currentStage === kStageFirstReturnDeuce ||
          currentSet.currentState.currentStage === kStageFirstReturnAd
        ) {
          currentSet.currentState.currentStage =
            currentSet.currentState.currentStage - 2;
          setCurrentStage(currentSet.currentState.currentStage);
          setCurrentPlayer(currentSet.currentState.currentServer);
          setCurrentSide(
            currentSet.currentState.currentServer.guid ===
              currentSet.currentState.topPlayer.guid
              ? 0
              : 1,
          );
          addToUndo();
        } else if (
          currentSet.currentState.currentStage === kStageSecondReturnDeuce ||
          currentSet.currentState.currentStage === kStageSecondReturnAd
        ) {
          endOfPoint(event);
        }
        break;
      }
    }
  };

  const doLetServe = (event) => {
    const cs = currentSet.currentState;
    let stage =
      cs.pointNumber % 2 === 0 ? kStageFirstServeDeuce : kStageFirstServeAd;
    if (event.subskill1 === kServeSecond) {
      stage =
        cs.pointNumber % 2 === 0
          ? kStageSecondServeDeuce
          : kStageSecondServeAd;
    }
    cs.currentStage = stage;
    cs.currentPlayer = event.player;
    cs.currentServer = event.player;
    // Side is which end of the court the server is on (top vs bottom), never
    // "is this player1" — comparing against player1 instead of topPlayer gave
    // the wrong side whenever player1 wasn't the top-of-court player.
    cs.currentSide = event.player.guid === cs.topPlayer.guid ? 0 : 1;
    cs.scores = { ...event.scores };
    cs.event = { ...event };
    setCurrentPlayer(cs.currentPlayer);
    setCurrentServer(cs.currentServer);
    setCurrentSide(cs.currentSide);
    setScores(cs.scores);
    setCurrentStage(cs.currentStage);
    // Keep the undo stack in sync with the corrected (let) state — without
    // this, Undo would skip straight past the let back to whatever preceded
    // it, since nothing was ever recorded for the let itself.
    addToUndo();
  };
  // go back to last ready to serve
  const restoreLastServeState = () => {
    for (let i = undoStack.length - 1; i >= 0; i--) {
      const st = undoStack[i];
      // `currentStage <= kStageSecondServeAd` alone means "ready to serve" —
      // that's true regardless of what led there. Also requiring the prior
      // event's result to be 0 was wrong: after a first-serve FAULT (result
      // -1), the resulting "ready for second serve" checkpoint was rejected,
      // so correcting a second serve to "Out" rolled all the way back past
      // the first-serve fault too — discarding it and miscoding the new out
      // as a first-serve fault instead of the double fault it actually was.
      if (st.currentStage <= kStageSecondServeAd) {
        // ready to serve
        // Preserve every event already coded except the serve currently being
        // re-entered — restoring the stale `st.events` snapshot would wipe out
        // legitimate prior events (e.g. a let) coded after this ready state.
        const preservedEvents = events.filter((e) => e !== currentEvent);
        const lastPreserved =
          preservedEvents.length > 0
            ? preservedEvents[preservedEvents.length - 1]
            : null;
        const restored = cloneState(st);
        setCurrentPlayer(restored.currentPlayer);
        setCurrentServer(restored.currentServer);
        setCurrentSide(restored.currentSide);
        setTopPlayer(restored.topPlayer);
        setBottomPlayer(restored.bottomPlayer);
        setScores(restored.scores);
        setCurrentStage(restored.currentStage);
        setPointNumber(restored.pointNumber);
        setEvents(preservedEvents);
        setSelectedEvent(lastPreserved);
        setCurrentEvent(lastPreserved);
        currentSet.events = preservedEvents;
        currentSet.currentState = {
          ...restored,
          events: preservedEvents,
          event: lastPreserved,
        };
        currentSet.scores = restored.scores;
        // Discard stale "future" entries beyond this point before recording
        // the restored state as the new latest entry.
        setUndoStack([
          ...undoStack.slice(0, i + 1),
          cloneState(currentSet.currentState),
        ]);
        setRedoStack([]);
        break;
      }
    }
  };

  // go back to when result was 1 (in play/undecided)
  const restoreLast0State = (skill) => {
    // Rally and Keypoint are the same shot type, just tagged by whether it
    // turned out to be decisive — treat them as one class when searching
    // history, since a Rally shot's last "in play" checkpoint was always
    // recorded as plain Rally, never Keypoint.
    const isRallyClass = (s) => s === kRally || s === kKeypoint;
    for (let i = undoStack.length - 1; i > 0; i--) {
      const st = undoStack[i];
      if (
        st.event.result === 1 &&
        (st.event.skill === skill ||
          (isRallyClass(st.event.skill) && isRallyClass(skill)))
      ) {
        const restored = cloneState(st);
        restored.scores = { ...restored.event.scores };
        setCurrentPlayer(restored.currentPlayer);
        setCurrentServer(restored.currentServer);
        setCurrentSide(restored.currentSide);
        setTopPlayer(restored.topPlayer);
        setBottomPlayer(restored.bottomPlayer);
        setScores(restored.scores);
        setCurrentStage(restored.currentStage);
        setPointNumber(restored.pointNumber);
        currentSet.currentState = restored;
        currentSet.scores = restored.scores;
        // Discard stale "future" entries beyond this point before recording
        // the restored state as the new latest entry.
        setUndoStack([...undoStack.slice(0, i + 1), cloneState(restored)]);
        setRedoStack([]);
        break;
      }
    }
  };

  // A serve into the net lands right on the net line. Ball positions are stored
  // in TS-normalised coordinates (0–100) where the net is the centre line at
  // y = 50, so snap the landing point onto it.
  const snapServeToNet = (event) => {
    if (event?.end) {
      event.end = { ...event.end, y: 50 };
    }
  };

  // A serve into the net is a fault (like OUT), but the tapped position is kept
  // and snapped onto the net rather than re-entered. When the serve had been
  // registered "in", convert it to a fault: first serve → second serve, second
  // serve → double fault.
  const doServeNetFromIn = () => {
    snapServeToNet(currentEvent);
    currentEvent.outcome = kServeNet;
    currentEvent.result = 0;
    const server = currentEvent.player;
    const side =
      server.guid === currentSet.currentState.topPlayer.guid ? 0 : 1;
    setCurrentPlayer(server);
    setCurrentServer(server);
    setCurrentSide(side);
    currentSet.currentState.currentPlayer = server;
    currentSet.currentState.currentServer = server;
    currentSet.currentState.currentSide = side;
    currentSet.currentState.event = { ...currentEvent };
    if (currentEvent.subskill1 === kServeSecond) {
      endOfPoint(currentEvent);
    } else {
      const pn = currentSet.currentState.pointNumber;
      const stage =
        pn % 2 === 0 ? kStageSecondServeDeuce : kStageSecondServeAd;
      currentSet.currentState.currentStage = stage;
      setCurrentStage(stage);
      addToUndo();
    }
  };

  const doOutcomeChanged = (outcome) => {
    const serveoutcomes = [
      kServeIn,
      kServeFootfault,
      kServeAce,
      kServeWinner,
      kServeLet,
      kServeOut,
      kServeNet,
    ];
    const returnoutcomes = [
      kReturnIn,
      kReturnUnforcedError,
      kReturnForcingError,
      kReturnWinner,
    ];
    const rallyoutcomes = [
      kRallyOutcomeIn,
      kRallyOutcomeWinner,
      kRallyOutcomeUnforcedError,
      kRallyOutcomeForcingError,
      kRallyOutcomePassingShot,
      kRallyOutcomeOutPassingShot,
      kRallyOutcomeNetted,
      kRallyOutcomePutAway,
    ];
    if (currentEvent.skill === 0) {
      // serve
      currentEvent.outcome = serveoutcomes[outcome];
      if (currentEvent.result === 1) {
        if (serveoutcomes[outcome] === kServeFootfault) {
          doFootFault(currentEvent);
          // forceUpdate((n) => !n);
        } else if (
          serveoutcomes[outcome] === kServeAce ||
          serveoutcomes[outcome] === kServeWinner
        ) {
          currentEvent.result = 3;
          endOfPoint(currentEvent);
        } else if (serveoutcomes[outcome] === kServeLet) {
          snapServeToNet(currentEvent);
          currentEvent.result = 1;
          doLetServe(currentEvent);
        } else if (serveoutcomes[outcome] === kServeOut) {
          restoreLastServeState();
          alert("Please enter ball position for serve out.");
        } else if (serveoutcomes[outcome] === kServeNet) {
          // Serve into the net is a fault (like OUT), keeping the tapped
          // position snapped onto the net rather than re-entering it.
          doServeNetFromIn();
        }
      } else if (currentEvent.result === 3) {
        if (serveoutcomes[outcome] === kServeIn) {
          currentEvent.result = 1;
          restoreLast0State(currentEvent.skill);
        } else if (serveoutcomes[outcome] === kServeFootfault) {
          doFootFault(currentEvent);
          // selectedEvent.outcome = serveoutcomes[outcome];
          if (
            currentSet.currentState.currentStage === kStageFirstReturnDeuce ||
            currentSet.currentState.currentStage === kStageFirstReturnAd
          ) {
            currentSet.currentState.currentStage =
              currentSet.currentState.currentStage - 2;
            setCurrentStage(currentSet.currentState.currentStage);
            setCurrentPlayer(currentSet.currentState.currentServer);
            setCurrentSide(
              currentSet.currentState.currentServer.guid === topPlayer.guid
                ? 0
                : 1,
            );
          }
          forceUpdate((n) => !n);
        } else if (
          serveoutcomes[outcome] === kServeAce ||
          serveoutcomes[outcome] === kServeWinner
        ) {
          forceUpdate((n) => !n);
        } else if (serveoutcomes[outcome] === kServeLet) {
          restoreLast0State(currentEvent.skill);
          snapServeToNet(currentEvent);
          currentEvent.result = 1;
          doLetServe(currentEvent);
        } else if (serveoutcomes[outcome] === kServeOut) {
          restoreLastServeState();
          alert("Please enter ball position for serve out.");
        } else if (serveoutcomes[outcome] === kServeNet) {
          restoreLast0State(currentEvent.skill);
          doServeNetFromIn();
        }
      } else if (currentEvent.result === 0) {
        if (serveoutcomes[outcome] === kServeIn) {
          currentEvent.result = 1;
          restoreLast0State(currentEvent.skill);
        } else if (
          serveoutcomes[outcome] === kServeAce ||
          serveoutcomes[outcome] === kServeWinner
        ) {
          currentEvent.result = 3;
          endOfPoint(currentEvent);
          // forceUpdate((n) => !n);
        } else if (serveoutcomes[outcome] === kServeLet) {
          restoreLast0State(currentEvent.skill);
          snapServeToNet(currentEvent);
          currentEvent.result = 1;
          doLetServe(currentEvent);
        } else if (serveoutcomes[outcome] === kServeOut) {
          restoreLastServeState();
          alert("Please enter ball position for serve out.");
        } else if (serveoutcomes[outcome] === kServeNet) {
          // Already registered as a fault — just relabel and snap onto the net.
          snapServeToNet(currentEvent);
          forceUpdate((n) => !n);
        }
      }
    } else if (currentEvent.skill === 1) {
      // return
      currentEvent.outcome = returnoutcomes[outcome];
      if (currentEvent.result === 1) {
        if (returnoutcomes[outcome] === kReturnWinner) {
          currentEvent.result = 3;
          endOfPoint(currentEvent);
        } else if (
          returnoutcomes[outcome] === kReturnUnforcedError ||
          returnoutcomes[outcome] === kReturnForcingError
        ) {
          currentEvent.result = 0;
          endOfPoint(currentEvent);
        }
      } else if (currentEvent.result === 3) {
        if (returnoutcomes[outcome] === kReturnIn) {
          restoreLast0State(currentEvent.skill);
          currentEvent.result = 1;
        } else if (
          returnoutcomes[outcome] === kReturnUnforcedError ||
          returnoutcomes[outcome] === kReturnForcingError
        ) {
          restoreLast0State(currentEvent.skill);
          currentEvent.result = 0;
          endOfPoint(currentEvent);
        }
      } else if (currentEvent.result === 0) {
        if (returnoutcomes[outcome] === kReturnIn) {
          restoreLast0State(currentEvent.skill);
          currentEvent.result = 1;
        } else if (returnoutcomes[outcome] === kReturnWinner) {
          restoreLast0State(currentEvent.skill);
          currentEvent.result = 3;
          endOfPoint(currentEvent);
        } else if (
          returnoutcomes[outcome] === kReturnUnforcedError ||
          returnoutcomes[outcome] === kReturnForcingError
        ) {
          forceUpdate((n) => !n);
        }
      }
    } else if (currentEvent.skill === 2 || currentEvent.skill === 3) {
      // rally or keypoint — a decisive (winner/error) rally shot IS a Keypoint
      // shot; reverting it back to "In" makes it a plain Rally shot again.
      currentEvent.outcome = rallyoutcomes[outcome];
      if (currentEvent.result === 1) {
        if (
          rallyoutcomes[outcome] === kRallyOutcomeWinner ||
          rallyoutcomes[outcome] === kRallyOutcomePassingShot ||
          rallyoutcomes[outcome] === kRallyOutcomePutAway
        ) {
          currentEvent.result = 3;
          currentEvent.skill = kKeypoint;
          endOfPoint(currentEvent);
        } else if (
          rallyoutcomes[outcome] === kRallyOutcomeUnforcedError ||
          rallyoutcomes[outcome] === kRallyOutcomeForcingError ||
          rallyoutcomes[outcome] === kRallyOutcomeNetted ||
          rallyoutcomes[outcome] === kRallyOutcomeOutPassingShot
        ) {
          currentEvent.result = 0;
          currentEvent.skill = kKeypoint;
          endOfPoint(currentEvent);
        }
      } else if (currentEvent.result === 3) {
        if (rallyoutcomes[outcome] === kRallyOutcomeIn) {
          restoreLast0State(currentEvent.skill);
          currentEvent.result = 1;
          currentEvent.skill = kRally;
        } else if (
          rallyoutcomes[outcome] === kRallyOutcomeUnforcedError ||
          rallyoutcomes[outcome] === kRallyOutcomeForcingError ||
          rallyoutcomes[outcome] === kRallyOutcomeNetted ||
          rallyoutcomes[outcome] === kRallyOutcomeOutPassingShot
        ) {
          restoreLast0State(currentEvent.skill);
          currentEvent.result = 0;
          currentEvent.skill = kKeypoint;
          endOfPoint(currentEvent);
        } else if (
          rallyoutcomes[outcome] === kRallyOutcomeWinner ||
          rallyoutcomes[outcome] === kRallyOutcomePassingShot ||
          rallyoutcomes[outcome] === kRallyOutcomePutAway
        ) {
          forceUpdate((n) => !n);
        }
      } else if (currentEvent.result === 0) {
        if (rallyoutcomes[outcome] === kRallyOutcomeIn) {
          restoreLast0State(currentEvent.skill);
          currentEvent.result = 1;
          currentEvent.skill = kRally;
        } else if (
          rallyoutcomes[outcome] === kRallyOutcomeWinner ||
          rallyoutcomes[outcome] === kRallyOutcomePassingShot ||
          rallyoutcomes[outcome] === kRallyOutcomePutAway
        ) {
          restoreLast0State(currentEvent.skill);
          currentEvent.result = 3;
          currentEvent.skill = kKeypoint;
          endOfPoint(currentEvent);
        } else if (
          rallyoutcomes[outcome] === kRallyOutcomeUnforcedError ||
          rallyoutcomes[outcome] === kRallyOutcomeForcingError ||
          rallyoutcomes[outcome] === kRallyOutcomeNetted ||
          rallyoutcomes[outcome] === kRallyOutcomeOutPassingShot
        ) {
          forceUpdate((n) => !n);
        }
      }
    }
    // Several branches above just relabel currentEvent.outcome within the
    // same result (e.g. Winner -> Passing Winner, Unforced Error -> Forced
    // Error) and only call forceUpdate — endOfPoint (called by the branches
    // that DO change the result) already persists, but those relabel-only
    // branches never did, leaving the edit unsaved until some later action.
    // Persisting here unconditionally closes that gap for every branch at
    // once; a harmless no-op/duplicate save for branches that already saved.
    localStorage.setItem("currentmatch", JSON.stringify(match));
    constructMatchData(match);
  };

  // Outcomes that are interchangeable WITHOUT changing the point's actual
  // result (winner stays a winner, error stays an error, …) — e.g. "Winner"
  // vs "Passing Winner"/"Put Away", or "Unforced Error" vs "Forced Error"/
  // "Passing Out"/"Netted". Grouped per skill; skill 3 (Keypoint) shares
  // rally's groups since it's the same shot type, just decisive.
  const RELABEL_GROUPS = {
    0: [
      [kServeFootfault, kServeNet],
      [kServeAce, kServeWinner],
    ],
    1: [[kReturnUnforcedError, kReturnForcingError]],
    2: [
      [kRallyOutcomeWinner, kRallyOutcomePassingShot, kRallyOutcomePutAway],
      [
        kRallyOutcomeUnforcedError,
        kRallyOutcomeForcingError,
        kRallyOutcomeOutPassingShot,
        kRallyOutcomeNetted,
      ],
    ],
  };
  RELABEL_GROUPS[3] = RELABEL_GROUPS[2];

  const canRelabelOutcome = (skill, fromOutcome, toOutcome) => {
    const groups = RELABEL_GROUPS[skill] ?? [];
    return groups.some(
      (g) => g.includes(fromOutcome) && g.includes(toOutcome),
    );
  };

  // Outcome selector dispatcher used by the event editor for whichever event
  // is currently SELECTED (which may be a past shot, not the live current
  // one). Changing the live current event's outcome goes through the full
  // doOutcomeChanged machinery unchanged (it can freely rewrite the score/
  // undo stack). Editing a PAST event only allows relabeling to a similar
  // outcome within the same result class — actually changing what a past
  // point's result was would require re-simulating every point after it,
  // which isn't supported; the coder must Undo back to that point instead.
  const doOutcomeChangedForSelected = (outcome) => {
    if (!selectedEvent) return;
    if (selectedEvent === currentEvent) {
      doOutcomeChanged(outcome);
      return;
    }
    const serveoutcomes = [
      kServeIn,
      kServeFootfault,
      kServeAce,
      kServeWinner,
      kServeLet,
      kServeOut,
      kServeNet,
    ];
    const returnoutcomes = [
      kReturnIn,
      kReturnUnforcedError,
      kReturnForcingError,
      kReturnWinner,
    ];
    const rallyoutcomes = [
      kRallyOutcomeIn,
      kRallyOutcomeWinner,
      kRallyOutcomeUnforcedError,
      kRallyOutcomeForcingError,
      kRallyOutcomePassingShot,
      kRallyOutcomeOutPassingShot,
      kRallyOutcomeNetted,
      kRallyOutcomePutAway,
    ];
    const list =
      selectedEvent.skill === 0
        ? serveoutcomes
        : selectedEvent.skill === 1
          ? returnoutcomes
          : rallyoutcomes;
    const newOutcome = list[outcome];
    if (newOutcome == null || newOutcome === selectedEvent.outcome) return;
    if (canRelabelOutcome(selectedEvent.skill, selectedEvent.outcome, newOutcome)) {
      selectedEvent.outcome = newOutcome;
      forceUpdate((n) => !n);
      // Persist immediately rather than waiting for some later action (or
      // unmount) to trigger a save — otherwise this edit to a PAST event sits
      // unsaved in memory only, and anyone the match is shared with (or the
      // owner themselves, on reload) keeps seeing the old outcome.
      localStorage.setItem("currentmatch", JSON.stringify(match));
      constructMatchData(match);
    } else {
      alert(
        "Only a similar outcome (e.g. Winner ↔ Passing Winner, Unforced Error ↔ Netted) can be changed for a past shot. To change the actual result, Undo back to this point and re-code it.",
      );
    }
  };

  const isEndOfSet = (st) => {
    // returns 0 if not end of set, 1 if player1 wins, 2 if player2 wins
    if (currentSet.isTiebreak) {
      let tiebreakTo = !match.tiebreakTo ? 7 : match.tiebreakTo;
      let tiebreakMargin =
        !match.tiebreakAdvantage || match.tiebreakAdvantage === 2 ? 2 : 1;
      if (
        st.scores.player1SetScore >= tiebreakTo &&
        st.scores.player1SetScore > st.scores.player2SetScore
      ) {
        return 1;
      } else if (
        st.scores.player2SetScore >= tiebreakTo &&
        st.scores.player2SetScore > st.scores.player1SetScore
      ) {
        return 2;
      }
      return 0;
    } else {
      if (
        st.scores.player1SetScore >= 6 &&
        st.scores.player1SetScore >= st.scores.player2SetScore + 2
      ) {
        return 1;
      } else if (
        st.scores.player2SetScore >= 6 &&
        st.scores.player2SetScore >= st.scores.player1SetScore + 2
      ) {
        return 2;
      }
      return 0;
    }
  };

  const checkEndOfSet = (st) => {
    // Returns true if a new set was started. When it did, this function has
    // already fully reset the undo/redo stacks and recorded the new set's
    // initial state as their sole entry — the caller must NOT separately push
    // `st` (the just-finished set's final state) afterwards, or the reset
    // gets clobbered and the new set's undo history is corrupted with the old
    // set's data.
    const eos = isEndOfSet(st);
    if (eos === 0) return false;
    setUndoStack([]);
    setRedoStack([]);
    const newset = createNewSet(currentSet.setNumber + 1);
    const newTop = newset.currentState.topPlayer;
    newset.currentState.currentPlayer = st.currentPlayer;
    newset.currentState.currentServer = st.currentPlayer;
    // Side must be computed against the SAME topPlayer stored in this
    // snapshot, not a possibly-stale component-state variable — otherwise the
    // persisted currentSide can disagree with the persisted topPlayer.
    newset.currentState.currentSide =
      st.currentPlayer.guid === newTop.guid ? 0 : 1;
    newset.firstServer = st.currentPlayer;
    setScores({ ...newset.scores });
    setCurrentPlayer(st.currentPlayer);
    setCurrentServer(st.currentPlayer);
    setCurrentSide(newset.currentState.currentSide);
    setPointNumber(0);
    setCurrentStage(kStageFirstServeDeuce);
    setCurrentSet(newset);
    addToUndo(newset.currentState);
    return true;
  };

  const isEndOfGame = (st) => {
    // returns 0 if not end of game, 1 if player1 wins, 2 if player2 wins
    if (currentSet.isTiebreak) {
      let tiebreakTo = !match.tiebreakTo ? 7 : match.tiebreakTo;
      let tiebreakMargin =
        !match.tiebreakAdvantage || match.tiebreakAdvantage === 2 ? 2 : 1;
      if (
        st.scores.player1GameScore >= tiebreakTo &&
        st.scores.player1GameScore >=
          st.scores.player2GameScore + tiebreakMargin
      ) {
        return 1;
      } else if (
        st.scores.player2GameScore >= tiebreakTo &&
        st.scores.player2GameScore >=
          st.scores.player1GameScore + tiebreakMargin
      ) {
        return 2;
      }
      return 0;
    } else {
      // No-Ad scoring: at deuce (3-3) the next point wins the game outright —
      // no two-point margin is required. Every other game (won 4-0/4-1/4-2)
      // already naturally satisfies the margin check too, so dropping it only
      // changes behaviour exactly at deuce, as intended.
      const isNoAd = match.scoreModeAdvantage === 1;
      if (
        st.scores.player1GameScore >= 4 &&
        (isNoAd ||
          st.scores.player1GameScore >= st.scores.player2GameScore + 2)
      ) {
        return 1;
      } else if (
        st.scores.player2GameScore >= 4 &&
        (isNoAd ||
          st.scores.player2GameScore >= st.scores.player1GameScore + 2)
      ) {
        return 2;
      }
      return 0;
    }
  };

  const checkEndOfGame = (st) => {
    // Mutates and returns `{ st, newSetStarted }`. `st` is always fully
    // self-consistent (currentServer/currentSide/topPlayer/bottomPlayer all
    // agree with each other) — computed directly from `st` itself, never from
    // component state (which may not have applied its last update yet, since
    // React setters are asynchronous). That self-consistency is what makes it
    // safe to persist/undo/resume from directly.
    const eog = isEndOfGame(st);
    if (eog === 1 || eog === 2) {
      if (eog === 1) {
        st.scores.player1SetScore = st.scores.player1SetScore + 1;
      } else {
        st.scores.player2SetScore = st.scores.player2SetScore + 1;
      }
      st.scores.player1GameScore = 0;
      st.scores.player2GameScore = 0;
      st.currentServer =
        st.currentServer.guid === player1.guid ? player2 : player1;
      st.currentPlayer = st.currentServer;
      st.pointNumber = 0;
      st.currentStage = kStageFirstServeDeuce;
      st.event = { ...currentEvent };
      st.events = currentSet.events;

      // Ends are swapped after every odd number of games played in this set
      // (standard tennis end-change rule). Compute — and the resulting side —
      // directly from `st`, so the persisted snapshot is always consistent.
      const gamesPlayed = st.scores.player1SetScore + st.scores.player2SetScore;
      if (gamesPlayed % 2 === 1) {
        const newTop = st.topPlayer.guid === player1.guid ? player2 : player1;
        st.topPlayer = newTop;
        st.bottomPlayer = newTop.guid === player1.guid ? player2 : player1;
        if (totalGames() === 0) match.topPlayer = st.topPlayer;
      }
      st.currentSide = st.currentServer.guid === st.topPlayer.guid ? 0 : 1;

      currentSet.scores = { ...st.scores };
      currentSet.currentState = { ...st };
      setCurrentServer(st.currentServer);
      setCurrentPlayer(st.currentServer);
      setTopPlayer(st.topPlayer);
      setBottomPlayer(st.bottomPlayer);
      setCurrentSide(st.currentSide);
      setPointNumber(0);
      setCurrentStage(kStageFirstServeDeuce);
      setScores({ ...st.scores });
    }
    // Only ever check for a just-finished set (or that a tiebreak should now
    // start) immediately after a game actually just ended above — a set can
    // never end mid-game. Running this check unconditionally on every point
    // meant that re-coding/correcting a point in an ALREADY-completed set
    // (e.g. going back to fix set 2 after sets 3/4 already exist) would
    // instantly re-trigger "end of set" on the very next point — since that
    // set's SetScore already satisfied the >=6-games threshold from before —
    // spawning a duplicate new set and jumping forward every single time.
    let newSetStarted = false;
    if (eog === 1 || eog === 2) {
      const tba = match.tiebreakAfter ? match.tiebreakAfter : 12;
      const tb =
        st.scores.player1SetScore === st.scores.player2SetScore &&
        st.scores.player1SetScore + st.scores.player2SetScore === tba;
      if (tb) {
        currentSet.isTiebreak = true;
      } else {
        newSetStarted = checkEndOfSet(st);
      }
    }
    return { st, newSetStarted };
  };

  const endOfPoint = (event) => {
    let st = { ...currentSet.currentState };
    // Clone the scores before mutating — `sc` must never be the SAME object
    // as `currentSet.currentState.scores` (or any earlier undo-stack entry
    // that might still reference it), or recording this point would silently
    // rewrite the score of a state that was already recorded as history.
    var sc = { ...currentSet.currentState.scores };
    if (event.result === 3) {
      if (event.player.guid === player1.guid) {
        sc.player1GameScore = sc.player1GameScore + 1;
      } else {
        sc.player2GameScore = sc.player2GameScore + 1;
      }
    } else if (event.result === 0) {
      if (event.player.guid === player1.guid) {
        sc.player2GameScore = sc.player2GameScore + 1;
      } else {
        sc.player1GameScore = sc.player1GameScore + 1;
      }
    }

    const pn = pointNumber + 1;
    setPointNumber(pn);
    st.pointNumber = pn;
    setScores(sc);
    st.scores = sc;
    currentSet.scores = { ...sc };

    if (currentSet.isTiebreak) {
      let tp = topPlayer;
      let bp = bottomPlayer;
      if ((sc.player1GameScore + sc.player2GameScore) % 6 === 0) {
        tp = topPlayer.guid === player1.guid ? player2 : player1;
        bp = bottomPlayer.guid === player1.guid ? player2 : player1;
        setTopPlayer(tp);
        setBottomPlayer(bp);
      }
      const cserver =
        pn % 2 === 0
          ? currentServer
          : currentServer.guid === player1.guid
            ? player2
            : player1;
      setCurrentPlayer(cserver);
      st.currentPlayer = cserver;
      setCurrentServer(cserver);
      st.currentServer = cserver;
      // The crossover swap above must also be recorded on `st` itself — the
      // side comparison below uses the NEW `tp`, so leaving st.topPlayer/
      // bottomPlayer at their old values would make the persisted snapshot
      // internally inconsistent (currentSide computed against a topPlayer
      // that isn't the one actually stored).
      st.topPlayer = tp;
      st.bottomPlayer = bp;
      setCurrentSide(cserver.guid === tp.guid ? 0 : 1);
      st.currentSide = cserver.guid === tp.guid ? 0 : 1;
      if (pn % 2 === 0) {
        st.currentStage = kStageFirstServeDeuce;
        setCurrentStage(kStageFirstServeDeuce);
      } else {
        st.currentStage = kStageFirstServeAd;
        setCurrentStage(kStageFirstServeAd);
      }
    } else {
      setCurrentPlayer(currentServer);
      st.currentPlayer = currentServer;
      setCurrentSide(currentServer.guid === topPlayer.guid ? 0 : 1);
      st.currentSide = currentServer.guid === topPlayer.guid ? 0 : 1;
      if (pn % 2 === 0) {
        st.currentStage = kStageFirstServeDeuce;
        setCurrentStage(kStageFirstServeDeuce);
      } else {
        st.currentStage = kStageFirstServeAd;
        setCurrentStage(kStageFirstServeAd);
      }
    }
    st.event = { ...event };
    st.events = currentSet.events;
    currentSet.currentState = { ...st };
    const { st: finalSt, newSetStarted } = checkEndOfGame(st);
    if (!newSetStarted) {
      // Single, authoritative push for this point-ending event — checkEndOfSet
      // already handled its own reset + push when a new set was started, so
      // pushing here too would corrupt that reset with stale data.
      currentSet.currentState = { ...finalSt };
      setUndoStack((prev) => [...prev, cloneState(finalSt)]);
      setRedoStack([]);
    }
    localStorage.setItem("currentmatch", JSON.stringify(match));
    constructMatchData(match);
    return finalSt;
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
    if (isFullscreen) {
      handle.exit();
      document.body.style.overflow = "auto";
    } else {
      handle.enter();
      document.body.style.overflow = "contain";
    }
  };

  const toggleVideo = () => {
    const showvideo = !isShowingVideo;
    setIsShowingVideo(showvideo);
    doSizing(showvideo);
    forceUpdate((n) => !n);
  };

  // const toggleOrientation = () => {
  //   getRightPanelSize();
  //   setIsCourtVertical((prev) => !prev);
  //   forceUpdate((n) => !n);
  // };

  const toggleServer = () => {
    const pl = currentServer.guid === player1.guid ? player2 : player1;
    setCurrentServer(pl);
    setCurrentPlayer(pl);
    if (totalGames() === 0) {
      match.firstServer = pl;
    }
    setCurrentSide(pl.guid === topPlayer.guid ? 0 : 1);
    forceUpdate((n) => !n);
  };

  const togglePlayerSide = () => {
    const tp = topPlayer.guid === player1.guid ? player2 : player1;
    const bp = bottomPlayer.guid === player1.guid ? player2 : player1;
    setTopPlayer(tp);
    setBottomPlayer(bp);
    if (totalGames() === 0) {
      match.topPlayer = tp;
    }
    setCurrentSide(currentServer.guid === tp.guid ? 0 : 1);
    forceUpdate((n) => !n);
  };

  const doSizing = (showvideo) => {
    const portrait = window.innerHeight > window.innerWidth;
    setIsPortrait(portrait);
  };

  const totalGames = () => {
    let totalgames = 0;
    for (var mset of match?.sets) {
      if (mset.scores) {
        totalgames += mset.scores.player1SetScore + mset.scores.player2SetScore;
      }
    }
    return totalgames;
  };

  const getNextServer = () => {
    let matchFirstServer = match.firstServer ?? match.player1;
    if (!match.sets || match.sets.length === 0) {
      return matchFirstServer;
    }
    const totalgames = totalGames();
    let nextServer =
      totalgames % 2 === 0
        ? matchFirstServer
        : matchFirstServer.guid === player1.guid
          ? player2
          : player1;
    return nextServer;
  };

  const getTopPlayer = () => {
    let matchTopPlayer = match.topPlayer ? match.topPlayer : match.player1;
    if (!match.sets || match.sets.length === 0) {
      return matchTopPlayer;
    }
    let totalgames = totalGames();
    if (totalgames === 0) {
      return matchTopPlayer;
    }
    const xx = (totalgames + 1) % 4;
    let topPlayer =
      xx === 0 || xx === 1
        ? matchTopPlayer
        : matchTopPlayer.guid === player1.guid
          ? player2
          : player1;
    return topPlayer;
  };

  const createNewSet = (sn) => {
    const cserver = getNextServer();
    const ctopplayer = getTopPlayer();
    let newset = {
      setNumber: sn,
      scores: {
        player1GameScore: 0,
        player2GameScore: 0,
        player1SetScore: 0,
        player2SetScore: 0,
      },
      firstServer: cserver,
      events: [],
    };
    if (!match.sets) match.sets = [];
    match.sets.push(newset);
    setCurrentSet(newset);
    newset.currentState = {
      scores: {
        player1GameScore: 0,
        player2GameScore: 0,
        player1SetScore: 0,
        player2SetScore: 0,
      },
      currentPlayer: cserver,
      currentServer: cserver,
      currentStage: kStageFirstServeDeuce,
      currentSide: cserver.guid === ctopplayer.guid ? 0 : 1,
      topPlayer: ctopplayer,
      bottomPlayer: ctopplayer.guid === player1.guid ? player2 : player1,
      pointNumber: 0,
      event: null,
      events: [],
    };
    setCurrentPlayer(newset.currentState.currentPlayer);
    setCurrentServer(newset.currentState.currentServer);
    setCurrentStage(newset.currentState.currentStage);
    setCurrentSide(newset.currentState.currentSide);
    setTopPlayer(newset.currentState.topPlayer);
    setBottomPlayer(newset.currentState.bottomPlayer);
    setPointNumber(0);
    setScores({ ...newset.scores });
    setEvents(newset.events);

    if (match.isDoubles) {
      // Real doubles teams can re-choose their serving order and deuce/ad
      // receiving formation each set — blocks coding until the "Doubles set
      // up" modal is submitted (see onCreateEvent, which then auto-rotates
      // for the rest of the set without asking again).
      setTeamADeuceReceiver(null);
      setTeamAAdReceiver(null);
      setTeamBDeuceReceiver(null);
      setTeamBAdReceiver(null);
      setPointServerIndividual(null);
      setPointReceiverIndividual(null);
      setNeedsDoublesSetup(true);
    }

    return newset;
  };

  // Switch the screen to edit an already-loaded set (from the set selector).
  const selectSetForEditing = (set) => {
    if (!set) return;
    if (!set.currentState) {
      const lastEvent = set.events?.length
        ? set.events[set.events.length - 1]
        : null;
      set.currentState = {
        scores: { ...set.scores },
        currentPlayer: player1,
        currentServer: player1,
        currentStage: kStageFirstServeDeuce,
        currentSide: 0,
        topPlayer: player1,
        bottomPlayer: player2,
        pointNumber: 0,
        event: lastEvent,
        events: set.events,
      };
    }
    const cs = set.currentState;
    setCurrentSet(set);
    setEvents(set.events ?? []);
    setScores(set.scores ?? cs.scores ?? {});
    setCurrentPlayer(cs.currentPlayer);
    setCurrentServer(cs.currentServer);
    setCurrentStage(cs.currentStage);
    setCurrentSide(cs.currentSide);
    setTopPlayer(cs.topPlayer);
    setBottomPlayer(cs.bottomPlayer);
    setPointNumber(cs.pointNumber ?? 0);
    const cevent =
      cs.event ??
      (set.events?.length ? set.events[set.events.length - 1] : null);
    setSelectedEvent(cevent);
    setCurrentEvent(cevent);
    // Clone before seeding the undo stack — it must never be the SAME object
    // as the live currentState, or a later in-place mutation of the live
    // state would corrupt this baseline history entry too.
    setUndoStack([cloneState(cs)]);
    setRedoStack([]);
    setDeleteFromIdx(null);
  };

  // Add a brand-new empty set and switch to it for coding.
  const addNewSet = () => {
    const nextNum = (match.sets?.length ?? 0) + 1;
    setUndoStack([]);
    setRedoStack([]);
    const ns = createNewSet(nextNum);
    addToUndo(ns.currentState);
    setDeleteFromIdx(null);
  };

  // Permanently remove a whole set (and every point coded in it) from the
  // match. If the deleted set was the one being edited, switch to whichever
  // set took its place in the array (or the last remaining one).
  const deleteSet = (set) => {
    if (!set || !match.sets) return;
    const idx = match.sets.indexOf(set);
    if (idx === -1) return;
    if (
      !window.confirm(
        `Delete Set ${set.setNumber ?? idx + 1}? This permanently removes every point coded in it and cannot be undone.`,
      )
    ) {
      return;
    }
    const wasActive = set === currentSet;
    match.sets.splice(idx, 1);

    if (match.sets.length === 0) {
      // Nothing left — start a fresh Set 1.
      setUndoStack([]);
      setRedoStack([]);
      const ns = createNewSet(1);
      addToUndo(ns.currentState);
      setDeleteFromIdx(null);
    } else if (wasActive) {
      const next = match.sets[idx] ?? match.sets[match.sets.length - 1];
      selectSetForEditing(next);
    } else {
      forceUpdate((n) => !n);
    }
    localStorage.setItem("currentmatch", JSON.stringify(match));
    constructMatchData(match);
  };

  const getPlayer = (playerNo) => {
    let pl = null;
    if (match.isDoubles) {
      if (playerNo === 0) {
        pl = match.teamA.player1;
      } else if (playerNo === 1) {
        pl = match.teamA.player2;
      } else if (playerNo === 2) {
        pl = match.teamB.player1;
      } else if (playerNo === 3) {
        pl = match.teamB.player2;
      }
    } else {
      if (playerNo === 0) {
        pl = match.player1;
      } else if (playerNo === 1) {
        pl = match.player2;
      }
    }
    return pl;
  };

  const convertDBEventToCodingEvent = (e, yScale = 1) => {
    const num = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    // PSTS/DB stores positions in a 0–100 (across) space. Imported data uses a
    // 0–200 along-court axis (net at y = 100); the coding court's TS space is
    // 0–100 with the net at y = 50. `yScale` (0.5 for 0–200 data, 1 for data
    // already in the 0–100 coding space) maps the along-court value in.
    const pt = (s) => {
      const p = stringToPoint(s);
      return { x: num(p.x), y: num(p.y) * yScale };
    };
    const evOut = {
      start: pt(e.startBall),
      end: pt(e.endBall),
      result: num(e.result),
      skill: num(e.skill),
      subskill1: num(e.subskill1),
      subskill2: num(e.subskill2),
      outcome: num(e.outcome),
      hand: num(e.hand),
      // Doubles: `player` must stay TEAM-level (see finaliseCreateEvent) —
      // getPlayer(playerNo) resolves the INDIVIDUAL, stored separately below.
      player: match.isDoubles
        ? num(e.playerNo) < 2
          ? match.teamA
          : match.teamB
        : getPlayer(num(e.playerNo)),
      playerNo: num(e.playerNo),
      videoTime: num(e.videoDuration),
      timestampseconds: e.timestamp
        ? new Date(e.timestamp).getTime() / 1000
        : null,
      reach: num(e.reach),
      scores: {
        player1GameScore: num(e.playerGameScore),
        player2GameScore: num(e.oppositionGameScore),
        player1SetScore: num(e.playerSetScore),
        player2SetScore: num(e.oppositionSetScore),
      },
    };
    if (match.isDoubles) {
      const indiv = getPlayer(num(e.playerNo));
      if (indiv) {
        evOut.individualPlayer = indiv;
        evOut.individualPlayerGuid = indiv.guid;
      }
      if (e.serveFormation !== undefined) evOut.serveFormation = num(e.serveFormation);
      if (e.netPosition !== undefined) evOut.netPosition = num(e.netPosition);
      if (e.returnFormation !== undefined) evOut.returnFormation = num(e.returnFormation);
    }
    return evOut;
  };

  const shouldShowBrowserExitConfirmDialog = true;
  window.onbeforeunload = function () {
    // Best-effort capture of the exact playback position for a tab close or
    // hard refresh, which unmounts nothing and so would otherwise skip the
    // mount effect's cleanup entirely.
    const vt = playerRef.current?.getCurrentTime?.();
    if (typeof vt === "number" && !Number.isNaN(vt)) {
      match.videoTime = vt;
      try {
        localStorage.setItem("currentmatch", JSON.stringify(match));
      } catch (e) {}
    }
    if (shouldShowBrowserExitConfirmDialog) {
      // The default message CANNOT be overridden
      return "Changes that you made may not be saved.";
    } else {
      return null;
    }
  };

  useEffect(() => {}, [
    currentEvent?.hand,
    currentEvent && currentEvent.outcome && currentEvent.subskill2,
  ]);

  // Keep the newest event scrolled into view at the bottom of the events list.
  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ block: "nearest" });
  }, [events.length]);

  // Re-code the current event at its new (redrawn) position, now that the
  // undo triggered by onUpdateEvent has actually applied — this effect runs
  // after that render commits, so currentStage/currentPlayer/etc here are the
  // freshly-restored (pre-event) values, not the stale pre-undo ones.
  useEffect(() => {
    if (!pendingRedraw) return;
    const { start, end, result, preserve } = pendingRedraw;
    setPendingRedraw(null);
    onCreateEvent({ start, end, result, netHint: null }, preserve);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRedraw]);

  // Keyboard shortcut key bindings are user-remappable (see the Keyboard
  // Shortcuts modal) and persisted to localStorage so remaps survive reloads.
  const persistShortcutKeys = (keys) => {
    setShortcutKeys(keys);
    localStorage.setItem(SHORTCUT_KEYS_STORAGE_KEY, JSON.stringify(keys));
  };

  const resetShortcutKeys = () => {
    const defaults = {};
    for (const a of SHORTCUT_ACTIONS) defaults[a.id] = a.defaultKey;
    persistShortcutKeys(defaults);
  };

  // While remapping (remapListeningId set), the next keypress becomes that
  // shortcut's new key; if that key is already assigned to another shortcut,
  // the two are swapped rather than left with a silent duplicate binding.
  useEffect(() => {
    if (!remapListeningId) return;
    const onRemapKeyDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") {
        setRemapListeningId(null);
        return;
      }
      const newKey = e.key.toLowerCase();
      if (newKey.length !== 1) return;
      const next = { ...shortcutKeys };
      const conflictId = Object.keys(next).find(
        (id) => id !== remapListeningId && next[id] === newKey,
      );
      if (conflictId) next[conflictId] = shortcutKeys[remapListeningId];
      next[remapListeningId] = newKey;
      persistShortcutKeys(next);
      setRemapListeningId(null);
    };
    document.addEventListener("keydown", onRemapKeyDown, true);
    return () => document.removeEventListener("keydown", onRemapKeyDown, true);
  }, [remapListeningId, shortcutKeys]);

  // Event Editor keyboard shortcuts — act on whichever event is currently
  // SELECTED in the editor (the live current one, or a past shot clicked
  // from the list), mirroring exactly what clicking the corresponding
  // Event Editor button would do (same handlers, same persistence/undo
  // rules). Ignored while typing in an input/textarea elsewhere on the page.
  useEffect(() => {
    const isTypingTarget = (el) =>
      el &&
      (el.tagName === "INPUT" ||
        el.tagName === "TEXTAREA" ||
        el.isContentEditable);

    const serveoutcomes = [
      kServeIn,
      kServeFootfault,
      kServeAce,
      kServeWinner,
      kServeLet,
      kServeOut,
      kServeNet,
    ];
    const returnoutcomes = [
      kReturnIn,
      kReturnUnforcedError,
      kReturnForcingError,
      kReturnWinner,
    ];
    const rallyoutcomes = [
      kRallyOutcomeIn,
      kRallyOutcomeWinner,
      kRallyOutcomeUnforcedError,
      kRallyOutcomeForcingError,
      kRallyOutcomePassingShot,
      kRallyOutcomeOutPassingShot,
      kRallyOutcomeNetted,
      kRallyOutcomePutAway,
    ];
    const returnss2 = [
      kRallyDrive,
      kRallySlice,
      kRallyTopspin,
      kRallyApproach,
      kRallyLob,
      kRallyDropshot,
      kRallyPowerShot,
      kRallyFlat,
      kRallyKick,
    ];
    const rallyss2 = [
      kRallySlice,
      kRallyTopspin,
      kRallyVolley,
      kRallyOverhead,
      kRallySmash,
      kRallyLob,
      kRallyDropshot,
      kRallyDriveVolley,
      kRallyHalfVolley,
      kRallyServeVolley,
      kRallyApproach,
      kRallyPassingShot,
      kRallyPowerShot,
      kRallyFlat,
      kRallyKick,
      kRallyDropVolley,
      kRallyVolleyPoach,
      kRallyBounceOverhead,
    ];
    const outcomesFor = (skill) =>
      skill === 0 ? serveoutcomes : skill === 1 ? returnoutcomes : rallyoutcomes;
    const subskill2sFor = (skill) => (skill === 1 ? returnss2 : rallyss2);

    // Both helpers silently no-op (via the -1 index guard) when the target
    // constant doesn't exist in this event's skill's list — e.g. pressing V
    // (Volley) on a Return, which has no Volley option.
    const setOutcomeByConstant = (event, constant) => {
      const idx = outcomesFor(event.skill).indexOf(constant);
      if (idx !== -1) doOutcomeChangedForSelected(idx);
    };
    const setSubskill2ByConstant = (event, constant) => {
      const idx = subskill2sFor(event.skill).indexOf(constant);
      if (idx !== -1) doSubskill2Changed(idx);
    };

    const onKeyDown = (e) => {
      if (remapListeningId) return;
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const action = SHORTCUT_ACTIONS.find(
        (a) => shortcutKeys[a.id] === e.key.toLowerCase(),
      );
      if (!action) return;

      // Video playback shortcuts act on the player regardless of whether an
      // event is selected.
      if (action.category === "video") {
        if (action.id === "togglePlayPause") {
          setIsPlaying((p) => !p);
        } else {
          const player = playerRef.current;
          if (player) {
            const cur = player.getCurrentTime?.() ?? 0;
            const dur = player.getDuration?.() ?? Infinity;
            let next = cur + (action.id === "advance1s" ? 1 : -1);
            if (next < 0.05) next = 0.05;
            if (Number.isFinite(dur) && next > dur) next = dur;
            player.seekTo(next, "seconds");
          }
        }
        e.preventDefault();
        return;
      }

      if (!selectedEvent) return;
      const ev = selectedEvent;
      switch (action.id) {
        case "toggleHand": // Toggle Backhand/Forehand
          if (ev.skill !== 0) doHandChanged(ev.hand === 0 ? 1 : 0);
          break;
        case "winner": // Winner
          setOutcomeByConstant(
            ev,
            ev.skill === 0
              ? kServeWinner
              : ev.skill === 1
                ? kReturnWinner
                : kRallyOutcomeWinner,
          );
          break;
        case "toggleError": { // Toggle Unforced Error/Forced Error
          if (ev.skill === 1) {
            setOutcomeByConstant(
              ev,
              ev.outcome === kReturnUnforcedError
                ? kReturnForcingError
                : kReturnUnforcedError,
            );
          } else if (ev.skill === 2 || ev.skill === 3) {
            setOutcomeByConstant(
              ev,
              ev.outcome === kRallyOutcomeUnforcedError
                ? kRallyOutcomeForcingError
                : kRallyOutcomeUnforcedError,
            );
          }
          break;
        }
        case "toggleSpin": // Toggle Slice/Topspin
          setSubskill2ByConstant(
            ev,
            ev.subskill2 === kRallySlice ? kRallyTopspin : kRallySlice,
          );
          break;
        case "volley":
          setSubskill2ByConstant(ev, kRallyVolley);
          break;
        case "passing": // Passing Winner (ball in) / Passing Out (ball out)
          setOutcomeByConstant(
            ev,
            ev.result === 0 ? kRallyOutcomeOutPassingShot : kRallyOutcomePassingShot,
          );
          break;
        case "lob":
          setSubskill2ByConstant(ev, kRallyLob);
          break;
        case "smash":
          setSubskill2ByConstant(ev, kRallySmash);
          break;
        case "drop":
          setSubskill2ByConstant(ev, kRallyDropshot);
          break;
        case "approach":
          setSubskill2ByConstant(ev, kRallyApproach);
          break;
        case "overhead":
          setSubskill2ByConstant(ev, kRallyOverhead);
          break;
        case "serveVolley":
          setSubskill2ByConstant(ev, kRallyServeVolley);
          break;
        case "power":
          setSubskill2ByConstant(ev, kRallyPowerShot);
          break;
        case "flat":
          setSubskill2ByConstant(ev, kRallyFlat);
          break;
        case "kick":
          setSubskill2ByConstant(ev, kRallyKick);
          break;
        case "dropVolley":
          setSubskill2ByConstant(ev, kRallyDropVolley);
          break;
        case "volleyPoach":
          setSubskill2ByConstant(ev, kRallyVolleyPoach);
          break;
        case "bounceOverhead":
          setSubskill2ByConstant(ev, kRallyBounceOverhead);
          break;
        case "driveVolley":
          setSubskill2ByConstant(ev, kRallyDriveVolley);
          break;
        case "cycleReach": // Cycle reach: Balanced -> Unbalanced -> Time Poor -> N/A -> …
          if (ev.skill !== 0) doReachChanged(((ev.reach ?? 0) + 1) % 4);
          break;
        default:
          return;
      }
      e.preventDefault();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [
    selectedEvent,
    currentEvent,
    doOutcomeChangedForSelected,
    doHandChanged,
    doSubskill2Changed,
    doReachChanged,
    shortcutKeys,
    remapListeningId,
  ]);

  // Mouse wheel over the video seeks it: a single frame per notch, more when
  // scrolling faster. Scroll up = advance, scroll down = rewind.
  useEffect(() => {
    const el = videoAreaRef.current;
    if (!el) return;
    const onWheel = (e) => {
      const player = playerRef.current;
      if (!player) return;
      e.preventDefault();
      const step =
        Math.max(1, Math.round(Math.abs(e.deltaY) / 100)) * FRAME_SECONDS;
      const cur = player.getCurrentTime?.() ?? 0;
      const dur = player.getDuration?.() ?? Infinity;
      let next = cur + (e.deltaY > 0 ? -step : step);
      // Keep just off 0 — seeking to exactly 0 shows an unpainted (blank) frame.
      if (next < 0.05) next = 0.05;
      if (Number.isFinite(dur) && next > dur) next = dur;
      player.seekTo(next, "seconds");
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [isShowingVideo, isPortrait]);

  useLayoutEffect(() => {
    doSizing(isShowingVideo);
  }, []);

  useEffect(() => {
    if (!courtRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setSizeCourt({ width, height });
    });
    ro.observe(courtRef.current);
    return () => ro.disconnect();
  }, [isShowingVideo, isPortrait]);

  useEffect(() => {
    if (!videoContainerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setSizeVideo({ width, height });
    });
    ro.observe(videoContainerRef.current);
    return () => ro.disconnect();
  }, [isShowingVideo]);

  useEffect(() => {
    doSizing(isShowingVideo);
  }, [isShowingVideo]);

  // Video scrub-to-seek: horizontal drag on the video area
  useEffect(() => {
    const el = videoAreaRef.current;
    if (!el) return;

    let startX = null;
    let startY = null;
    let startTime = null;
    let scrubActive = false;
    let moved = 0; // max distance travelled — used to tell a tap from a drag
    let lastTouch = 0; // guards against iPad's emulated mouse-after-touch events
    // Distinguishes a single tap (play/pause) from a double tap (+10s) — there's
    // no native "double tap" event here, so a short timer does it: a lone tap
    // fires after the wait, a follow-up tap within the window cancels it and
    // seeks forward instead.
    let lastTapTime = 0;
    let singleTapTimer = null;
    const DOUBLE_TAP_WINDOW = 350; // ms

    const onDown = (e) => {
      const isMouse = !e.touches;
      if (isMouse && Date.now() - lastTouch < 700) return; // emulated
      if (!isMouse) lastTouch = Date.now();
      const pt = e.touches ? e.touches[0] : e;
      startX = pt.clientX;
      startY = pt.clientY;
      startTime = playerRef.current?.getCurrentTime() ?? 0;
      scrubActive = false;
      moved = 0;
    };

    const onMove = (e) => {
      if (startX === null) return;
      const pt = e.touches ? e.touches[0] : e;
      const dx = pt.clientX - startX;
      const dy = pt.clientY - startY;
      moved = Math.max(moved, Math.hypot(dx, dy));
      // Begin scrubbing once horizontal movement clearly dominates.
      if (!scrubActive && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
        scrubActive = true;
        setIsScrubbing(true);
      }
      if (!scrubActive) return;
      e.preventDefault();
      const newTime = Math.max(0.05, startTime + dx * 0.1);
      playerRef.current?.seekTo(newTime, "seconds");
    };

    const reset = () => {
      startX = null;
      startY = null;
      scrubActive = false;
      setIsScrubbing(false);
    };

    // A tap (little movement, never became a scrub) toggles play/pause —
    // replacing the native controls we disabled to stop iOS dimming the video.
    // A SECOND tap landing shortly after instead advances the video 10 seconds.
    const onEnd = (e) => {
      const isMouse = !e.changedTouches;
      if (isMouse && Date.now() - lastTouch < 700) {
        reset();
        return; // emulated mouseup after a touch
      }
      if (!isMouse) lastTouch = Date.now();
      if (startX !== null && !scrubActive && moved < 12) {
        const now = Date.now();
        const isDoubleTap = now - lastTapTime < DOUBLE_TAP_WINDOW;
        lastTapTime = now;
        if (isDoubleTap) {
          if (singleTapTimer) {
            clearTimeout(singleTapTimer);
            singleTapTimer = null;
          }
          const player = playerRef.current;
          if (player) {
            const cur = player.getCurrentTime?.() ?? 0;
            const dur = player.getDuration?.() ?? Infinity;
            let next = cur + 10;
            if (Number.isFinite(dur) && next > dur) next = dur;
            player.seekTo(next, "seconds");
          }
        } else {
          singleTapTimer = setTimeout(() => {
            singleTapTimer = null;
            setIsPlaying((p) => !p);
          }, DOUBLE_TAP_WINDOW);
        }
      }
      reset();
    };

    el.addEventListener("mousedown", onDown);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseup", onEnd);
    el.addEventListener("mouseleave", reset);
    el.addEventListener("touchstart", onDown, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", reset, { passive: true });

    return () => {
      if (singleTapTimer) clearTimeout(singleTapTimer);
      el.removeEventListener("mousedown", onDown);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseup", onEnd);
      el.removeEventListener("mouseleave", reset);
      el.removeEventListener("touchstart", onDown);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", reset);
    };
  }, [isShowingVideo, isPortrait]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    // Shared helper: apply a coding-format set snapshot to all React state
    const restoreFromCodingSet = (cset) => {
      setCurrentSet(cset);
      setEvents(cset.events ?? []);
      setScores(cset.scores ?? cset.currentState?.scores ?? {});
      const cs = cset.currentState;
      if (cs) {
        setCurrentPlayer(cs.currentPlayer);
        setCurrentServer(cs.currentServer);
        setCurrentStage(cs.currentStage);
        setCurrentSide(cs.currentSide);
        setTopPlayer(cs.topPlayer);
        setBottomPlayer(cs.bottomPlayer);
        setPointNumber(cs.pointNumber ?? 0);
        const cevent =
          cs.event ??
          (cset.events?.length > 0
            ? cset.events[cset.events.length - 1]
            : null);
        setSelectedEvent(cevent);
        setCurrentEvent(cevent);
        // Clone before seeding the undo stack — never alias it to the live
        // currentState object (later in-place mutations would corrupt it).
        setUndoStack([cloneState(cs)]);
      }
    };

    // Load the whole match straight from `match.sets` — the exact same complete
    // source the Video Analysis screen reads. Every stored (DB-format) set is
    // converted to the coding event shape and kept; the user then picks which
    // set to edit (or adds a new one). No localStorage cache, no partial
    // "currentState" snapshots — the full event list always wins.
    if (!match.sets) match.sets = [];
    const isCodingEvent = (e) =>
      e && typeof e.scores === "object" && e.start != null;
    const allsets = match.sets.map((mset) => {
      const dbEvents = (mset.events ?? []).filter(Boolean);
      // Imported/PSTS data uses a 0–200 along-court axis (net at y = 100);
      // locally-coded data already sits in the coding court's 0–100 space
      // (net at y = 50). Detect which by the largest y in the set and halve
      // only the 0–200 data so both display in the right place.
      let maxY = 0;
      for (const e of dbEvents) {
        if (isCodingEvent(e)) continue;
        const sy = Math.abs(Number(stringToPoint(e.startBall)?.y) || 0);
        const ey = Math.abs(Number(stringToPoint(e.endBall)?.y) || 0);
        if (sy > maxY) maxY = sy;
        if (ey > maxY) maxY = ey;
      }
      const yScale = maxY > 110 ? 0.5 : 1;
      const convertedEvents = dbEvents.map((e) =>
        isCodingEvent(e) ? e : convertDBEventToCodingEvent(e, yScale),
      );
      return {
        setNumber: mset.setNumber ?? mset.number ?? 1,
        isTiebreak: mset.isTiebreak ?? false,
        scores: {
          player1GameScore: mset.scores?.player1GameScore ?? mset.playerGameScore ?? 0,
          player2GameScore: mset.scores?.player2GameScore ?? mset.oppositionGameScore ?? 0,
          player1SetScore: mset.scores?.player1SetScore ?? mset.playerScore ?? 0,
          player2SetScore: mset.scores?.player2SetScore ?? mset.oppositionScore ?? 0,
        },
        firstServer: null,
        events: convertedEvents,
        // Carry over the persisted live-coding state (who's serving, stage,
        // side, …) if this set has one — e.g. imported PSTS sets never had
        // one and fall back to a synthesised default below. Previously this
        // was always dropped here, so every reload of an actively-coded match
        // silently reset to "player1, first serve deuce" regardless of the
        // real (correctly saved) state.
        currentState: mset.currentState ? { ...mset.currentState } : undefined,
      };
    });
    match.sets = allsets;

    if (!allsets.some((s) => s.events.length > 0)) {
      // Brand-new match — start a fresh Set 1.
      match.sets = [];
      const newset = createNewSet(1);
      // Clone before seeding the undo stack — the baseline entry must never
      // be the SAME object as the live currentState, or the first in-place
      // mutation of the live state (e.g. inside endOfPoint) would silently
      // corrupt this "history" entry too.
      setUndoStack([cloneState(newset.currentState)]);
    } else {
      // Select the last set for editing; users can switch via the set selector.
      const lastSet = allsets[allsets.length - 1];
      const lastEvent = lastSet.events.length
        ? lastSet.events[lastSet.events.length - 1]
        : null;
      if (match.isDoubles) {
        // Reconstruct rotation from what's already coded instead of asking
        // again — a set with no events yet (e.g. brand-new set 2) still
        // needs the "Doubles set up" modal, so only skip it on success.
        setNeedsDoublesSetup(!reconstructDoublesRotation(lastSet.events));
      }
      if (lastSet.currentState) {
        // The event conversion above rebuilds fresh event objects, so any
        // stale `.event`/`.events` references from the persisted state must
        // be repointed at the newly-converted array.
        lastSet.currentState.event = lastEvent;
        lastSet.currentState.events = lastSet.events;
      } else {
        lastSet.currentState = {
          scores: { ...lastSet.scores },
          currentPlayer: player1,
          currentServer: player1,
          currentStage: kStageFirstServeDeuce,
          currentSide: 0,
          topPlayer: player1,
          bottomPlayer: player2,
          pointNumber: 0,
          event: lastEvent,
          events: lastSet.events,
        };
      }
      restoreFromCodingSet(lastSet);
    }
    constructMatchData(match).then((md) => setMatchData(md));

    doSizing(isShowingVideo);

    function handleWindowResize() {
      doSizing(isShowingVideo);
    }
    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
      // Capture the exact playback position so resuming this match later can
      // seek straight back to it. Read fresh at unmount time (not captured
      // earlier in the effect) since that's the only moment this value is
      // meaningful — safe here as this effect only ever unmounts once, on
      // navigating away from the page.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const vt = playerRef.current?.getCurrentTime?.();
      if (typeof vt === "number" && !Number.isNaN(vt)) {
        match.videoTime = vt;
      }
      // Persist in-memory match state so re-entering this match can restore it.
      // `match` is mutated in place throughout the session so it is always current.
      try {
        localStorage.setItem("currentmatch", JSON.stringify(match));
      } catch (e) {}
      // Persist to the database too — not just localStorage — so the position
      // survives even if the user resumes from a different browser/device.
      constructMatchData(match);
    };
  }, []);

  const doScoreboard = () => {
    return (
      <div className="bg-base-100 border-2 border-base-300">
        <Scoreboard match={match} activeSet={currentSet} />
      </div>
    );
  };

  const doEventEditor = (portrait) => {
    return (
      <div className="overflow-auto bg-base-100 p-1 border-2 border-base-300 h-full">
        <EventEditor
          isPortrait={portrait}
          event={selectedEvent}
          isCurrentEvent={selectedEvent === currentEvent}
          player1={player1}
          player2={player2}
          isDoubles={match.isDoubles}
          doublesPlayers={[
            match.teamA?.player1,
            match.teamA?.player2,
            match.teamB?.player1,
            match.teamB?.player2,
          ]}
          onPlayerChanged={(player) => doPlayerChanged(player)}
          onHandChanged={(hand) => doHandChanged(hand)}
          onSubskill2Changed={(subskill2) => doSubskill2Changed(subskill2)}
          onOutcomeChanged={(outcome) => doOutcomeChangedForSelected(outcome)}
          onServeChanged={(serve) => doServeChanged(serve)}
          onReachChanged={(reach) => doReachChanged(reach)}
          onServeFormationChanged={(serveFormation) => doServeFormationChanged(serveFormation)}
          onNetPositionChanged={(netPosition) => doNetPositionChanged(netPosition)}
          onReturnFormationChanged={(returnFormation) => doReturnFormationChanged(returnFormation)}
        />
      </div>
    );
  };

  const eventClassName = (event) => {
    var str =
      selectedEvent === event
        ? "bg-primary/50 flex-col p-1 border-b border-base-300"
        : "bg-base-100 flex-col p-1 border-b border-base-300";
    return str;
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    // Clicking any event in the list enters "edit mode" — the court switches
    // to reviewing/correcting that past shot rather than coding a new one.
    // "Resume Coding" (in the video toolbar) is the only way back out.
    if (!isEditMode) {
      // Only capture on the way IN — clicking another event while already in
      // edit mode must not overwrite the original pre-edit position.
      preEditVideoTimeRef.current = playerRef.current?.getCurrentTime?.() ?? null;
    }
    setIsEditMode(true);
    var vt = event?.videoTime ?? null;
    if (vt != null && playerRef?.current) {
      playerRef.current.seekTo(vt, "seconds");
    }
  };

  // Replace the selected event's ball path with a newly-drawn one — called
  // from CodingCourt while in edit mode. If the redraw doesn't change the
  // shot's result (still in, or still out), it's a pure position correction:
  // just move the path, keeping every other attribute untouched. Only when
  // editing the CURRENT (most recent) event AND the result actually changes
  // (e.g. in → out) do we undo it and re-code it fresh at the new position —
  // safely recalculating scores/stage — while preserving hand/subskill1/
  // subskill2/reach exactly as they were (see the `preserve` handling in
  // processEvent/onCreateEvent).
  const onUpdateEvent = (updatedEvent) => {
    if (!selectedEvent) return;
    const isEditingCurrent = selectedEvent === currentEvent;
    // selectedEvent.result is already in the final (0=error/1=in play/3=winner)
    // scale; updatedEvent.result is CodingCourt's raw ball-position tap (0=in,
    // -1=out). "wasIn"/"isIn" here mean "not an error", the closest equivalent
    // across both scales.
    const wasIn = selectedEvent.result !== 0;
    const isIn = updatedEvent.result === 0;
    if (isEditingCurrent && isIn !== wasIn) {
      const preserve = {
        hand: selectedEvent.hand,
        subskill1: selectedEvent.subskill1,
        subskill2: selectedEvent.subskill2,
        reach: selectedEvent.reach,
        serveFormation: selectedEvent.serveFormation,
        netPosition: selectedEvent.netPosition,
        returnFormation: selectedEvent.returnFormation,
      };
      onUndo();
      setIsEditMode(false);
      // Defer the re-code until after the undo's state updates (scores,
      // stage, side, server, …) have actually applied — React state setters
      // are asynchronous, so reading currentStage/currentPlayer/etc in this
      // same tick would still see the pre-undo values.
      setPendingRedraw({
        start: updatedEvent.start,
        end: updatedEvent.end,
        result: updatedEvent.result,
        preserve,
      });
      return;
    }
    selectedEvent.start = updatedEvent.start;
    selectedEvent.end = updatedEvent.end;
    forceUpdate((n) => !n);
  };

  // Leave edit mode and go back to coding the current (in-progress) event,
  // restoring the video to exactly where it was before edit mode was entered
  // (falling back to the current event's recorded time if that wasn't captured).
  const resumeCoding = () => {
    setIsEditMode(false);
    setSelectedEvent(currentEvent);
    const vt = preEditVideoTimeRef.current ?? currentEvent?.videoTime ?? null;
    if (vt != null && playerRef?.current) {
      playerRef.current.seekTo(vt, "seconds");
    }
    preEditVideoTimeRef.current = null;
  };

  // No-Ad scoring: at deuce (40-40) the returner — not the normal point-count
  // parity — gets to choose whether to receive the deciding point from the
  // deuce or ad court. `isNoAdDecidingPoint` (computed where used, from live
  // `scores`/`currentStage`) detects that moment; this just applies the
  // returner's choice by overriding the auto-computed stage for that point.
  const chooseNoAdSide = (stage) => {
    setCurrentStage(stage);
    if (currentSet?.currentState) {
      currentSet.currentState.currentStage = stage;
    }
    localStorage.setItem("currentmatch", JSON.stringify(match));
    forceUpdate((n) => !n);
  };

  // A rally (point) is a run of consecutive events that share the same score.
  // The live scores only advance when a point ends, so every event coded within
  // a point carries an identical score signature.
  const rallyScoreSig = (e) => {
    const s = e?.scores ?? {};
    return `${s.player1SetScore ?? 0}-${s.player2SetScore ?? 0}-${s.player1GameScore ?? 0}-${s.player2GameScore ?? 0}`;
  };

  // Group events into rallies in chronological order (index 0 = oldest rally).
  const groupEventsIntoRallies = (evts) => {
    const chron = [...(evts ?? [])].sort(
      (a, b) => (a.timestampseconds ?? 0) - (b.timestampseconds ?? 0),
    );
    const rallies = [];
    let sig = null;
    for (const e of chron) {
      const s = rallyScoreSig(e);
      if (s !== sig || rallies.length === 0) {
        rallies.push({ events: [] });
        sig = s;
      }
      rallies[rallies.length - 1].events.push(e);
    }
    return rallies;
  };

  // Push the live match state onto the screen (scoreboard, court, editor …).
  const applyMatchState = (st) => {
    if (!st) return;
    setScores(st.scores);
    setCurrentPlayer(st.currentPlayer);
    setCurrentServer(st.currentServer);
    setCurrentStage(st.currentStage);
    setCurrentSide(st.currentSide);
    setTopPlayer(st.topPlayer);
    setBottomPlayer(st.bottomPlayer);
    setPointNumber(st.pointNumber ?? 0);
    setCurrentEvent(st.event);
    setSelectedEvent(st.event);
    setEvents(st.events ?? []);
    // Components that read straight from `match`/`currentSet` (Scoreboard,
    // CodingCourt) need the mutated objects plus a re-render nudge.
    currentSet.scores = st.scores;
    currentSet.events = st.events ?? [];
    currentSet.currentState = st;
    forceUpdate((n) => !n);
  };

  // Delete the selected rally (chronological index `fromIdx`) and every newer
  // rally, then restore the match state (scores, server, sides, stage, point
  // number …) to how it was before those rallies were coded. The deletion is
  // itself recorded on the undo stack so it can be undone.
  const deleteRalliesFrom = (fromIdx) => {
    const rallies = groupEventsIntoRallies(events);
    if (fromIdx == null || fromIdx < 0 || fromIdx >= rallies.length) return;

    const keptEvents = rallies.slice(0, fromIdx).flatMap((r) => r.events);
    const keptCount = keptEvents.length;
    // First event of the first deleted rally — a serve whose stored score and
    // player are the authoritative "start of next point" (= end of last kept
    // rally) state.
    const firstDeleted = rallies[fromIdx].events[0];

    // The undo snapshot taken when there were exactly `keptCount` events holds
    // the stage/side/ends/point-number for that boundary.
    let boundary = null;
    for (let i = undoStack.length - 1; i >= 0; i--) {
      if ((undoStack[i].events?.length ?? 0) === keptCount) {
        boundary = undoStack[i];
        break;
      }
    }
    if (!boundary) boundary = undoStack[0] ?? currentSet.currentState ?? {};

    const resolvePlayer = (p) =>
      p?.guid && p.guid === player2.guid ? player2 : player1;
    const serverObj = resolvePlayer(firstDeleted?.player ?? boundary.currentServer);
    const topObj = resolvePlayer(boundary.topPlayer ?? topPlayer);
    const bottomObj = topObj.guid === player1.guid ? player2 : player1;

    const restored = {
      scores: {
        ...(firstDeleted?.scores ??
          boundary.scores ?? {
            player1GameScore: 0,
            player2GameScore: 0,
            player1SetScore: 0,
            player2SetScore: 0,
          }),
      },
      currentPlayer: serverObj,
      currentServer: serverObj,
      // The next shot after removing whole rallies is always a first serve; the
      // court (deuce vs ad) follows the point-score parity, matching the engine.
      currentStage: kStageFirstServeDeuce,
      currentSide: serverObj.guid === topObj.guid ? 0 : 1,
      topPlayer: topObj,
      bottomPlayer: bottomObj,
      pointNumber: boundary.pointNumber ?? 0,
      event: keptEvents.length ? keptEvents[keptEvents.length - 1] : null,
      events: keptEvents,
    };
    const gamePointTotal =
      (restored.scores.player1GameScore ?? 0) +
      (restored.scores.player2GameScore ?? 0);
    restored.currentStage =
      gamePointTotal % 2 === 0 ? kStageFirstServeDeuce : kStageFirstServeAd;

    applyMatchState(restored);

    // Record the deletion so a single Undo brings the rallies (and prior state)
    // back — the previous stack top is the full pre-deletion state.
    setUndoStack((prev) => [...prev, JSON.parse(JSON.stringify(restored))]);
    setRedoStack([]);
    setDeleteFromIdx(null);
    localStorage.setItem("currentmatch", JSON.stringify(match));
    constructMatchData(match);
  };

  // Selecting a rally marks it and every newer rally for deletion (only the
  // latest rallies can be removed). Clicking the current boundary clears it.
  const toggleRallyDeleteSelection = (idx) => {
    setDeleteFromIdx((prev) => (prev === idx ? null : idx));
  };

  const confirmDeleteRallies = () => {
    const rallies = groupEventsIntoRallies(events);
    if (deleteFromIdx == null) return;
    const count = rallies.length - deleteFromIdx;
    if (count <= 0) return;
    if (
      window.confirm(
        `Delete the last ${count} ${count === 1 ? "rally" : "rallies"}? The match state will be restored to before ${count === 1 ? "it" : "them"}.`,
      )
    ) {
      deleteRalliesFrom(deleteFromIdx);
    }
  };

  // Score labels for the events list, mirroring how Video Analysis renders the
  // stored `realScores`. Game points normally use tennis notation (00/15/30/40/
  // A); inside a tiebreak — either a 6-6 set tiebreak or a match tiebreak played
  // in place of the final set (scoreModeTiebreak) — they are shown as raw
  // points instead.
  const tennisPoint = (p, o) => {
    const labels = ["00", "15", "30", "40"];
    if (p < 4) return labels[p] ?? String(p);
    return p > o ? "A" : "40";
  };

  const isTiebreakScores = (s) => {
    const p1Set = s.player1SetScore ?? 0;
    const p2Set = s.player2SetScore ?? 0;
    // Match tiebreak played in place of the final (3rd) set.
    if (currentSet?.setNumber === 3 && (match.scoreModeTiebreak ?? 0) > 0)
      return true;
    // Regular 6-6 set tiebreak.
    if (currentSet?.isTiebreak && p1Set === 6 && p2Set === 6) return true;
    return false;
  };

  const gameScoreLabel = (event) => {
    const s = event?.scores;
    if (!s || typeof s !== "object") return "";
    const p1g = s.player1GameScore ?? 0;
    const p2g = s.player2GameScore ?? 0;
    if (isTiebreakScores(s)) return `${p1g}-${p2g}`;
    return `${tennisPoint(p1g, p2g)}-${tennisPoint(p2g, p1g)}`;
  };

  // Rally header score: set score followed by game score, e.g. "2-3 40-30".
  const rallyScoreLabel = (event) => {
    const s = event?.scores;
    if (!s || typeof s !== "object") return "";
    const p1Set = s.player1SetScore ?? 0;
    const p2Set = s.player2SetScore ?? 0;
    return `${p1Set}-${p2Set} ${gameScoreLabel(event)}`;
  };

  // Doubles-only formation label shown under a serve/return event — index
  // order matches EventEditor.jsx's serveFormations/netPositions/
  // returnFormations lists (and the iOS app's, see pstsFile.js's
  // parsePlayerPositions).
  const SERVE_FORMATIONS = ["Traditional", "Eye", "Squeeze", "Tandem", "2 Back"];
  const NET_POSITIONS = ["Hold", "Switch", "Cover Middle", "Left", "Right"];
  const RETURN_FORMATIONS = ["Traditional", "2 Back"];
  const doublesFormationLabel = (event) => {
    if (!match.isDoubles) return null;
    if (event.skill === 0) {
      return `${SERVE_FORMATIONS[event.serveFormation ?? 0]} / ${NET_POSITIONS[event.netPosition ?? 0]}`;
    }
    if (event.skill === 1) {
      return RETURN_FORMATIONS[event.returnFormation ?? 0];
    }
    return null;
  };

  const doEventsList = () => {
    const rallies = groupEventsIntoRallies(events);
    const total = rallies.length;
    const selCount = deleteFromIdx == null ? 0 : total - deleteFromIdx;
    const sets = match.sets ?? [];
    // No-Ad deciding point: the score just reached deuce (3-3) in a No-Ad set
    // and we're about to serve — the returner chooses which court to receive
    // from, rather than it being dictated by the usual point-count parity.
    const isNoAdDecidingPoint =
      match.scoreModeAdvantage === 1 &&
      currentSet &&
      !currentSet.isTiebreak &&
      scores.player1GameScore === 3 &&
      scores.player2GameScore === 3 &&
      (currentStage === kStageFirstServeDeuce ||
        currentStage === kStageFirstServeAd);
    return (
      <div className="bg-base-100 overflow-hidden border-2 border-base-300 p-1 h-full flex flex-col">
        {/* Set selector — pick which set to edit, or add a new one. */}
        <div className="flex items-center gap-1 px-1 pb-1 mb-1 border-b border-base-300 shrink-0 flex-wrap">
          <span className="text-xs font-semibold mr-1">Sets:</span>
          {sets.map((s, i) => {
            const isActive = s === currentSet;
            return (
              <div key={i} className="join">
                <button
                  className={`join-item btn btn-xs ${isActive ? "btn-primary" : "btn-ghost"}`}
                  title={`Edit set ${s.setNumber ?? i + 1}`}
                  onClick={() => selectSetForEditing(s)}
                >
                  {s.setNumber ?? i + 1}
                </button>
                <button
                  className={`join-item btn btn-xs btn-square ${isActive ? "btn-primary" : "btn-ghost"}`}
                  title={`Delete set ${s.setNumber ?? i + 1}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSet(s);
                  }}
                >
                  <TrashIcon className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          <button
            className="btn btn-xs btn-outline gap-1"
            title="Add a new set"
            onClick={addNewSet}
          >
            + New Set
          </button>
        </div>
        {isNoAdDecidingPoint && (
          <div className="flex items-center justify-between gap-1 px-1 py-1 mb-1 rounded bg-info/10 shrink-0 flex-wrap">
            <span className="text-xs font-medium">
              No-Ad deciding point — returner's choice:
            </span>
            <div className="flex gap-1">
              <button
                className={`btn btn-xs ${currentStage === kStageFirstServeDeuce ? "btn-info" : "btn-outline"}`}
                onClick={() => chooseNoAdSide(kStageFirstServeDeuce)}
              >
                Deuce Court
              </button>
              <button
                className={`btn btn-xs ${currentStage === kStageFirstServeAd ? "btn-info" : "btn-outline"}`}
                onClick={() => chooseNoAdSide(kStageFirstServeAd)}
              >
                Ad Court
              </button>
            </div>
          </div>
        )}
        {isEditMode && !isShowingVideo && (
          <div className="flex items-center justify-between gap-1 px-1 py-1 mb-1 rounded bg-warning/10 shrink-0">
            <span className="text-xs font-medium">Editing a past shot</span>
            <button
              className="btn btn-warning btn-xs gap-1"
              title="Leave edit mode and return to coding, restoring the video position from before editing"
              onClick={resumeCoding}
            >
              <PlayCircleIcon className="size-4" />
              Resume Coding
            </button>
          </div>
        )}
        {selCount > 0 && (
          <div className="flex items-center justify-between gap-1 px-1 py-1 mb-1 rounded bg-error/10 shrink-0">
            <span className="text-xs font-medium">
              {selCount} {selCount === 1 ? "rally" : "rallies"} selected
            </span>
            <div className="flex gap-1">
              <button
                className="btn btn-ghost btn-xs"
                onClick={() => setDeleteFromIdx(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-error btn-xs gap-1"
                onClick={confirmDeleteRallies}
              >
                <TrashIcon className="w-3.5 h-3.5" />
                Delete
              </button>
            </div>
          </div>
        )}
        <div className="overflow-auto flex-1">
          {rallies
            .map((rally, idx) => ({ rally, idx }))
            .map(({ rally, idx }) => {
              const selected = deleteFromIdx != null && idx >= deleteFromIdx;
              return (
                <div
                  key={idx}
                  className={`mb-1 rounded border ${
                    selected ? "border-error bg-error/5" : "border-base-300"
                  }`}
                >
                  <div className="flex items-center gap-1 px-1 py-0.5 bg-base-200 text-xs font-semibold">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      title="Select this rally and all newer ones for deletion"
                      checked={selected}
                      onChange={() => toggleRallyDeleteSelection(idx)}
                    />
                    <span>Rally {idx + 1}</span>
                    <span className="ml-auto">
                      {rallyScoreLabel(rally.events[0])}
                    </span>
                  </div>
                  {rally.events.map((event, ei) => (
                    <div
                      className={eventClassName(event)}
                      key={ei}
                      onClick={() => handleSelectEvent(event)}
                    >
                      <div className="flex justify-between">
                        <div className="font-medium text-xs">
                          {playerInitialAndName(
                            match.isDoubles && event.individualPlayer
                              ? { player: event.individualPlayer }
                              : event,
                          )}
                        </div>
                        <div className="font-medium text-xs">
                          {gameScoreLabel(event)}
                        </div>
                      </div>
                      <div className="font-normal text-xs">
                        {eventString(event)}
                      </div>
                      {doublesFormationLabel(event) && (
                        <div className="font-normal text-[10px] text-base-content/60">
                          {doublesFormationLabel(event)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          <div ref={eventsEndRef} />
        </div>
      </div>
    );
  };

  const doPlaybackRate = (rate) => {
    setPlaybackRate(parseFloat(rate));
    // playerRef?.current?.playbackRate(parseFloat(rate));
  };

  let seekInterval = 2.5;
  function seekBack() {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() - seekInterval);
  }

  // Function to seek forwards and display a timecode overlay
  function seekForward() {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() + seekInterval);
  }

  const doSwipe = (interval) => {
    if (interval > 0) {
      seekInterval = interval;
      seekForward();
    } else {
      seekInterval = -interval;
      seekBack();
    }
  };

  const swipedRight = () => {
    seekForward();
  };

  const swipedLeft = () => {
    seekBack();
  };

  // Step exactly one video frame forward/back — the smallest possible seek
  // increment, for precise frame-by-frame review.
  const stepFrame = (direction) => {
    const player = playerRef.current;
    if (!player) return;
    const cur = player.getCurrentTime?.() ?? 0;
    const dur = player.getDuration?.() ?? Infinity;
    let next = cur + direction * FRAME_SECONDS;
    if (next < 0.05) next = 0.05;
    if (Number.isFinite(dur) && next > dur) next = dur;
    player.seekTo(next, "seconds");
  };

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      // toast.success("User Swiped Velocity: " + eventData.velocity);
      seekInterval = 2.5 + eventData.velocity;
    },
    onSwipedLeft: () => swipedLeft(),
    onSwipedRight: () => swipedRight(),
  });

  const doCodingCourt = (vertical) => {
    // When editing the CURRENT (most recent) event, the zone check used while
    // redrawing its path (to detect an in/out result change) must use the
    // stage/side as they were BEFORE that event — not the live values, which
    // already reflect its own outcome (e.g. a stage that's since moved on to
    // "return"). That's exactly the undo-stack entry recorded just before it.
    const isEditingCurrent =
      isEditMode && selectedEvent != null && selectedEvent === currentEvent;
    let courtStage = currentStage;
    let courtSide = currentPlayer?.guid === topPlayer?.guid ? 0 : 1;
    if (isEditingCurrent && undoStack.length >= 2) {
      const preState = undoStack[undoStack.length - 2];
      // Use the exact side/stage that were live at the moment this event was
      // originally coded — not a re-derived guid comparison — so a single
      // click behaves identically to normal (non-edit) coding.
      courtStage = preState.currentStage;
      courtSide = preState.currentSide;
    }
    return (
      <div
        ref={courtRef}
        className="w-full h-full overflow-hidden bg-blue-400 border-2 border-base-300"
      >
        <CodingCourt
          isVertical={vertical}
          courtSize={sizeCourt}
          topPlayer={topPlayer}
          bottomPlayer={bottomPlayer}
          currentPlayer={currentPlayer}
          currentStage={courtStage}
          currentSide={courtSide}
          pointNumber={pointNumber}
          selectedEvent={selectedEvent}
          isEditMode={isEditMode}
          onCreateEvent={(event) => onCreateEvent(event)}
          onUpdateEvent={(event) => onUpdateEvent(event)}
          onDeleteEvent={(event) => onDeleteEvent(event)}
          onToggleHand={() => toggleCurrentEventHand()}
          onToggleSpin={() => toggleCurrentEventSpin()}
          updated={(n) => !n}
        />
      </div>
    );
  };

  const doVideoPlayer = () => {
    const videoH = sizeVideo.height > 44 ? sizeVideo.height - 44 : 200;
    const fmtTime = (s) => {
      const t = Math.max(0, Math.floor(s || 0));
      const mm = Math.floor(t / 60);
      const ss = String(t % 60).padStart(2, "0");
      return `${mm}:${ss}`;
    };
    return (
      <div
        ref={videoContainerRef}
        className="flex flex-col w-full h-full overflow-hidden"
      >
        <div
          ref={videoAreaRef}
          className={`flex-1 min-h-0 select-none${isScrubbing ? " cursor-ew-resize" : ""}`}
        >
          <ReactPlayer
            ref={playerRef}
            url={resolveVideoUrl(videoFilePath)}
            playing={isPlaying}
            muted={isMuted}
            controls={false}
            width="100%"
            height={`${videoH}px`}
            playbackRate={playbackRate}
            progressInterval={100}
            onReady={() => {
              const p = playerRef.current;
              // Resume exactly where the user left off last time this match's
              // video was open, but only on the very first ready callback —
              // later re-fires (e.g. switching sets/video URL) shouldn't jump
              // the user back to the old, unrelated position.
              if (!hasRestoredVideoPositionRef.current) {
                hasRestoredVideoPositionRef.current = true;
                const savedTime = match?.videoTime;
                if (typeof savedTime === "number" && savedTime > 0.05) {
                  p?.seekTo(savedTime, "seconds");
                  return;
                }
              }
              // Nudge off exact 0 so a first frame is decoded and painted —
              // otherwise the video area stays blank until the first seek.
              if (p && (p.getCurrentTime?.() ?? 0) < 0.05) {
                p.seekTo(0.05, "seconds");
              }
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onDuration={(d) => setVideoDuration(d)}
            onProgress={({ playedSeconds }) => {
              // Ignore updates while the user is dragging the seek bar, or the
              // thumb would jump back to the (stale) playback position.
              if (!isSeekBarDragging) setVideoCurrentTime(playedSeconds);
            }}
            playsinline
            config={{
              file: {
                attributes: { playsInline: true, controlsList: "nodownload" },
              },
              youtube: { playerVars: { controls: 0, playsinline: 1 } },
            }}
          />
        </div>
        {/* Progress / seek bar */}
        <div className="shrink-0 flex items-center gap-2 px-2 pt-1 bg-base-300">
          <span className="text-xs tabular-nums text-base-content/60 w-9 text-right">
            {fmtTime(videoCurrentTime)}
          </span>
          <input
            type="range"
            className="range range-xs flex-1"
            min={0}
            max={videoDuration > 0 ? videoDuration : 0.001}
            step={FRAME_SECONDS}
            value={Math.min(videoCurrentTime, videoDuration || 0)}
            onMouseDown={() => setIsSeekBarDragging(true)}
            onTouchStart={() => setIsSeekBarDragging(true)}
            onChange={(e) => {
              const t = parseFloat(e.target.value);
              setVideoCurrentTime(t);
              playerRef.current?.seekTo(t, "seconds");
            }}
            onMouseUp={() => setIsSeekBarDragging(false)}
            onTouchEnd={() => setIsSeekBarDragging(false)}
          />
          <span className="text-xs tabular-nums text-base-content/60 w-9">
            {fmtTime(videoDuration)}
          </span>
        </div>
        <div className="shrink-0 flex gap-2 p-1 bg-base-300" {...handlers}>
          <ChevronDoubleLeftIcon
            className="btn-toolbar size-5"
            title="Back 10 seconds"
            onClick={() => {
              doSwipe(-10);
            }}
          />
          <ChevronLeftIcon
            className="btn-toolbar size-5"
            title="Back 1 second"
            onClick={() => {
              doSwipe(-1);
            }}
          />
          <ArrowLeftIcon
            className="btn-toolbar size-4 mt-0.5"
            title="Previous frame"
            onClick={() => stepFrame(-1)}
          />
          <select
            className="w-16 mx-2 select-generic text-base-content/50 rounded-md h-6 text-xs"
            value={playbackRate}
            onChange={(e) => doPlaybackRate(e.target.value)}
          >
            <option value="0.25">0.25x</option>
            <option value="0.5">0.5x</option>
            <option value="1">1x</option>
            <option value="1.5">1.5x</option>
            <option value="2">2x</option>
            <option value="3">3x</option>
            <option value="4">4x</option>
          </select>
          <ArrowRightIcon
            className="btn-toolbar size-4 mt-0.5"
            title="Next frame"
            onClick={() => stepFrame(1)}
          />
          <ChevronRightIcon
            className="btn-toolbar size-5"
            title="Forward 1 second"
            onClick={() => {
              doSwipe(1);
            }}
          />
          <ChevronDoubleRightIcon
            className="btn-toolbar size-5"
            title="Forward 10 seconds"
            onClick={() => {
              doSwipe(10);
            }}
          />
          {isEditMode && (
            <button
              className="btn btn-warning btn-xs gap-1"
              title="Leave edit mode and return to coding, restoring the video position from before editing"
              onClick={resumeCoding}
            >
              <PlayCircleIcon className="size-4" />
              Resume Coding
            </button>
          )}
          {isMuted ? (
            <SpeakerXMarkIcon
              className="btn-toolbar size-5 ml-auto"
              title="Unmute"
              onClick={() => setIsMuted(false)}
            />
          ) : (
            <SpeakerWaveIcon
              className="btn-toolbar size-5 ml-auto"
              title="Mute"
              onClick={() => setIsMuted(true)}
            />
          )}
        </div>
      </div>
    );
  };

  const doPortraitLayout = () => {
    if (!isShowingVideo) {
      // Left: [Scoreboard + EventsList (top, resizable)] / [EventEditor portrait (bottom, resizable)]
      // Right: CodingCourt portrait
      return (
        <PanelGroup orientation="horizontal" className="h-full">
          <Panel defaultSize={30} minSize={15} id="panelLeft">
            <PanelGroup
              orientation="vertical"
              className="h-full"
              units="pixels"
            >
              <Panel minSize={80} id="panelInfoTop">
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="shrink-0">{doScoreboard()}</div>
                  <div className="flex-1 min-h-0 overflow-hidden">
                    {doEventsList()}
                  </div>
                </div>
              </Panel>
              <PanelResizeHandle className="h-1.5 bg-base-300 hover:bg-primary transition-colors cursor-row-resize" />
              <Panel defaultSize={620} minSize={100} id="panelEditor">
                {doEventEditor(true)}
              </Panel>
            </PanelGroup>
          </Panel>
          <PanelResizeHandle className="w-1.5 bg-base-300 hover:bg-primary transition-colors cursor-col-resize" />
          <Panel defaultSize={70} minSize={30} id="panelCourt">
            {doCodingCourt(true)}
          </Panel>
        </PanelGroup>
      );
    } else {
      // Top row (resizable): Video player — full width, height = 9/16 * width
      // Bottom row (resizable): Three columns: [Scoreboard+EventsList] | [EventEditor portrait] | [CodingCourt portrait]
      return (
        <PanelGroup orientation="vertical" className="h-full">
          <Panel defaultSize={50} minSize={20} id="panelVideo">
            {doVideoPlayer()}
          </Panel>
          <PanelResizeHandle className="h-1.5 bg-base-300 hover:bg-primary transition-colors cursor-row-resize" />
          <Panel defaultSize={50} minSize={30} id="panelBottom">
            <PanelGroup orientation="horizontal" className="h-full">
              <Panel defaultSize={30} minSize={15} id="panelInfo">
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="shrink-0">{doScoreboard()}</div>
                  <div className="flex-1 min-h-0 overflow-hidden">
                    {doEventsList()}
                  </div>
                </div>
              </Panel>
              <PanelResizeHandle className="w-1.5 bg-base-300 hover:bg-primary transition-colors cursor-col-resize" />
              <Panel defaultSize={35} minSize={15} id="panelEditor">
                {doEventEditor(true)}
              </Panel>
              <PanelResizeHandle className="w-1.5 bg-base-300 hover:bg-primary transition-colors cursor-col-resize" />
              <Panel defaultSize={35} minSize={20} id="panelCourt">
                {doCodingCourt(true)}
              </Panel>
            </PanelGroup>
          </Panel>
        </PanelGroup>
      );
    }
  };

  const doLandscapeLayout = () => {
    if (!isShowingVideo) {
      // Top row (resizable): Two columns — [Scoreboard+EventsList] | [EventEditor landscape]
      // Bottom row: CodingCourt landscape
      return (
        <PanelGroup orientation="vertical" className="h-full" units="pixels">
          <Panel defaultSize={240} minSize={100} id="panelInfoRow">
            <PanelGroup
              orientation="horizontal"
              className="h-full"
              units="pixels"
            >
              <Panel defaultSize={240} minSize={160} id="panelInfoLeft">
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="shrink-0">{doScoreboard()}</div>
                  <div className="flex-1 min-h-0 overflow-hidden">
                    {doEventsList()}
                  </div>
                </div>
              </Panel>
              <PanelResizeHandle className="w-1.5 bg-base-300 hover:bg-primary transition-colors cursor-col-resize" />
              <Panel minSize={200} id="panelEditor">
                {doEventEditor(false)}
              </Panel>
            </PanelGroup>
          </Panel>
          <PanelResizeHandle className="h-1.5 bg-base-300 hover:bg-primary transition-colors cursor-row-resize" />
          <Panel minSize={150} id="panelCourt">
            {doCodingCourt(false)}
          </Panel>
        </PanelGroup>
      );
    } else {
      // Court fills the full window height on the right, sized to only as wide
      // as its aspect ratio needs (no wasted width). The left column stacks the
      // video above the scoreboard/events-list + event editor, with the events
      // list kept narrow so the event editor gets more room.
      return (
        <div className="flex h-full">
          <div className="flex-1 min-w-0 h-full">
            <PanelGroup orientation="vertical" className="h-full" units="pixels">
              <Panel minSize={160} id="panelVideo">
                {doVideoPlayer()}
              </Panel>
              <PanelResizeHandle className="h-1.5 bg-base-300 hover:bg-primary transition-colors cursor-row-resize" />
              <Panel defaultSize={240} minSize={120} id="panelBottom">
                <PanelGroup orientation="horizontal" className="h-full">
                  <Panel defaultSize={20} minSize={10} id="panelInfo">
                    <div className="flex flex-col h-full overflow-hidden">
                      <div className="shrink-0">{doScoreboard()}</div>
                      <div className="flex-1 min-h-0 overflow-hidden">
                        {doEventsList()}
                      </div>
                    </div>
                  </Panel>
                  <PanelResizeHandle className="w-1.5 bg-base-300 hover:bg-primary transition-colors cursor-col-resize" />
                  <Panel defaultSize={80} minSize={20} id="panelEditor">
                    {doEventEditor(false)}
                  </Panel>
                </PanelGroup>
              </Panel>
            </PanelGroup>
          </div>
          {/* Court — full height, width follows the court's aspect ratio only.
              The content is absolutely positioned so the canvas's intrinsic
              size can't stretch this column beyond its aspect ratio. */}
          <div
            className="h-full shrink-0 relative"
            style={{ aspectRatio: "256 / 668" }}
          >
            <div className="absolute inset-0">{doCodingCourt(true)}</div>
          </div>
        </div>
      );
    }
  };

  const doSaveMatchState = () => {
    setShowMatchStateModal(false);
    setScores({ ...currentSet.scores });
    setCurrentPlayer(currentSet.currentState.currentPlayer);
    setCurrentServer(currentSet.currentState.currentServer);
    setCurrentStage(currentSet.currentState.currentStage);
    setCurrentSide(
      currentSet.currentState.currentServer.guid === topPlayer.guid ? 0 : 1,
    );
    forceUpdate((n) => !n);
  };

  return (
    <>
      <FullScreen handle={handle}>
        <div
          className="flex flex-col bg-base-200 w-full overflow-hidden"
          style={{ height: "100dvh" }}
        >
          {/* ── Toolbar ── */}
          <div className="flex items-center gap-1 px-2 py-1 shrink-0 border-b border-base-300 bg-base-100">
            {/* 1.a Fullscreen */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              onClick={() => toggleFullscreen()}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="w-5 h-5" />
              ) : (
                <ArrowsPointingOutIcon className="w-5 h-5" />
              )}
            </button>

            {/* 1.b Players swapping sides */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip="Swap player sides"
              onClick={() => togglePlayerSide()}
            >
              {isPortrait ? (
                <ArrowsUpDownIcon className="w-5 h-5" />
              ) : (
                <ArrowsRightLeftIcon className="w-5 h-5" />
              )}
            </button>

            {/* 1.c Toggle current server */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip="Toggle server"
              onClick={() => toggleServer()}
            >
              <UserIcon className="w-5 h-5" />
            </button>

            {/* 1.d Undo */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip="Undo"
              disabled={undoStack.length <= 1}
              onClick={onUndo}
            >
              <Undo icon={true} className="w-5 h-5" />
            </button>

            {/* 1.e Redo */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip="Redo"
              disabled={redoStack.length === 0}
              onClick={onRedo}
            >
              <Redo icon={true} className="w-5 h-5" />
            </button>

            {/* 1.f Edit current match state */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip="Edit match state"
              onClick={onEditMatchState}
            >
              <EditMatchState icon={true} className="w-5 h-5" />
            </button>

            {/* Generate random set */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip="Generate random set"
              onClick={onGenerateRandomSet}
            >
              <SparklesIcon className="w-5 h-5" />
            </button>

            {/* Ball Tracker Lab (opens in a new tab to preserve coding state) */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip="Ball Tracker Lab (CV)"
              onClick={() => window.open("/ball-tracker", "_blank", "noopener")}
            >
              <ViewfinderCircleIcon className="w-5 h-5" />
            </button>

            {/* Keyboard/mouse shortcuts help */}
            <button
              className="btn btn-sm btn-ghost tooltip tooltip-bottom"
              data-tip="Keyboard shortcuts"
              onClick={() => setShowShortcutsModal(true)}
            >
              <InformationCircleIcon className="w-5 h-5" />
            </button>

            <div className="ml-auto" />

            {/* 1.g Video toggle — right justified */}
            {isShowingVideo ? (
              <>
                <input
                  type="file"
                  id="selectedVideoFile"
                  style={{ display: "none" }}
                  onChange={handleVideoSelected}
                  onClick={(event) => {
                    event.target.value = null;
                  }}
                />
                {/* URL area: editing → input+Load, loaded → label */}
                {isEditingUrl ? (
                  <>
                    <input
                      type="url"
                      autoFocus
                      className="input input-bordered input-xs h-7 w-56 text-xs"
                      placeholder="https://..."
                      value={videoUrlInput}
                      onChange={(e) => setVideoUrlInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && videoUrlInput.trim()) {
                          applyVideoUrl(videoUrlInput.trim());
                          setIsEditingUrl(false);
                        } else if (e.key === "Escape") {
                          setIsEditingUrl(false);
                        }
                      }}
                    />
                    <button
                      className="btn btn-primary btn-xs h-7"
                      onClick={() => {
                        if (videoUrlInput.trim())
                          applyVideoUrl(videoUrlInput.trim());
                        setIsEditingUrl(false);
                      }}
                    >
                      Load
                    </button>
                  </>
                ) : (
                  videoFilePath &&
                  !videoFilePath.startsWith("blob:") && (
                    <span
                      className="text-xs text-base-content/60 max-w-[24rem] truncate"
                      title={videoFilePath}
                    >
                      {videoFilePath}
                    </span>
                  )
                )}
                <button
                  className="btn btn-sm btn-ghost tooltip tooltip-bottom"
                  data-tip="Enter online video URL"
                  onClick={() => {
                    setVideoUrlInput(
                      videoFilePath?.startsWith("blob:")
                        ? ""
                        : (videoFilePath ?? ""),
                    );
                    setIsEditingUrl(true);
                  }}
                >
                  <LinkIcon className="w-5 h-5" />
                </button>
                <button
                  className="btn btn-sm btn-ghost tooltip tooltip-bottom"
                  data-tip="Select local video file"
                  onClick={() =>
                    document.getElementById("selectedVideoFile").click()
                  }
                >
                  <FilmIcon className="w-5 h-5" />
                </button>
                <button
                  className="btn btn-sm btn-ghost tooltip tooltip-bottom"
                  data-tip="Hide video"
                  onClick={() => toggleVideo()}
                >
                  <TVCancel className="w-5 h-5" />
                </button>
              </>
            ) : (
              <button
                className="btn btn-sm btn-ghost tooltip tooltip-bottom"
                data-tip="Show video"
                onClick={() => toggleVideo()}
              >
                <TVRetro className="w-5 h-5" />
              </button>
            )}
          </div>
          {/* ── Layout ── */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {isPortrait ? doPortraitLayout() : doLandscapeLayout()}
          </div>
        </div>
      </FullScreen>

      {showMatchStateModal && (
        <div className="modal modal-open">
          <div className="modal-box w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-4 shrink-0">
              <h3 className="font-bold text-lg">Edit Match State</h3>
              <button
                className="btn btn-ghost btn-sm btn-square"
                onClick={() => setShowMatchStateModal(false)}
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <CurrentMatchState
                match={match}
                activeSet={currentSet}
                update={() => forceUpdate((n) => !n)}
                onSaveMatchState={() => doSaveMatchState()}
              />
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setShowMatchStateModal(false)}
          />
        </div>
      )}

      {showShortcutsModal && (
        <div className="modal modal-open">
          <div className="modal-box w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-4 shrink-0">
              <h3 className="font-bold text-lg">Mouse &amp; Keyboard Shortcuts</h3>
              <button
                className="btn btn-ghost btn-sm btn-square"
                onClick={() => setShowShortcutsModal(false)}
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <ul className="list-disc pl-5 space-y-3 text-sm">
                <li>
                  <span className="font-semibold">Mouse click</span> on the
                  video: toggles play/pause.
                </li>
                <li>
                  <span className="font-semibold">Mouse double click</span> on
                  the video: advances 10 seconds.
                </li>
                <li>
                  <span className="font-semibold">Mouse wheel</span> over the
                  video: seeks backward/forward. Scroll slowly to
                  increment/decrement a single frame at a time.
                </li>
                <li>
                  <span className="font-semibold">
                    Mouse right click
                  </span>{" "}
                  on the court after coding a return/rally shot: a single
                  click toggles forehand/backhand; a double click toggles
                  topspin/slice.
                </li>
              </ul>
              <div className="flex items-center justify-between mt-4 mb-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-base-content/50">
                  Keyboard Shortcuts
                </div>
                <button
                  className="btn btn-ghost btn-xs"
                  onClick={resetShortcutKeys}
                >
                  Reset to Defaults
                </button>
              </div>
              <p className="text-xs text-base-content/60 mb-2">
                Click a key below, then press any key to remap it (Esc
                cancels); remapping to a key already in use swaps the two
                shortcuts.
              </p>
              <div className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-1">
                Video Playback
              </div>
              <ul className="space-y-1 text-sm mb-3">
                {SHORTCUT_ACTIONS.filter((a) => a.category === "video").map(
                  (a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span>{a.label}</span>
                      <button
                        className={`kbd kbd-sm min-w-8 ${
                          remapListeningId === a.id ? "kbd-primary animate-pulse" : ""
                        }`}
                        title="Click, then press a key to reassign"
                        onClick={() => setRemapListeningId(a.id)}
                      >
                        {remapListeningId === a.id
                          ? "…"
                          : formatShortcutKey(shortcutKeys[a.id])}
                      </button>
                    </li>
                  ),
                )}
              </ul>
              <div className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-1">
                Event Editor
              </div>
              <p className="text-xs text-base-content/60 mb-2">
                Act on whichever event is selected in the Event Editor (not
                while typing in a text field). Only apply where relevant to
                that shot's skill.
              </p>
              <ul className="space-y-1 text-sm">
                {SHORTCUT_ACTIONS.filter((a) => a.category === "event").map(
                  (a) => (
                    <li
                      key={a.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <span>{a.label}</span>
                      <button
                        className={`kbd kbd-sm min-w-8 ${
                          remapListeningId === a.id ? "kbd-primary animate-pulse" : ""
                        }`}
                        title="Click, then press a key to reassign"
                        onClick={() => setRemapListeningId(a.id)}
                      >
                        {remapListeningId === a.id
                          ? "…"
                          : formatShortcutKey(shortcutKeys[a.id])}
                      </button>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setShowShortcutsModal(false)}
          />
        </div>
      )}

      {needsDoublesSetup && (() => {
        // Either team's player can be picked as the first server — once
        // picked, their team is inferred and the receiver picker is
        // restricted to the OTHER team's 2 players.
        const servingTeam = teamOfIndividual(setupFirstServer);
        const receivingTeam = servingTeam
          ? servingTeam.guid === match.teamA?.guid
            ? match.teamB
            : match.teamA
          : null;
        const pickFirstServer = (p) => {
          setSetupFirstServer(p);
          // A previously-picked receiver may no longer belong to the other
          // team once the server changes — clear it rather than leave a
          // stale/invalid selection in place.
          setSetupFirstReceiver(null);
        };
        return (
        <div className="modal modal-open">
          <div className="modal-box w-full max-w-sm">
            <h3 className="font-bold text-lg mb-4">Doubles set up</h3>
            <div className="mb-4">
              <div className="text-sm font-semibold mb-1">Who serves first?</div>
              <div className="flex flex-col gap-2">
                {[match.teamA, match.teamB].map((team, ti) => (
                  <div key={team?.guid ?? ti} className="flex gap-2">
                    {[team?.player1, team?.player2].map(
                      (p, i) =>
                        p && (
                          <button
                            key={p.guid ?? i}
                            className={`btn btn-sm ${
                              setupFirstServer?.guid === p.guid ? "btn-primary" : "btn-outline"
                            }`}
                            onClick={() => pickFirstServer(p)}
                          >
                            {playerInitialAndName({ player: p })}
                          </button>
                        ),
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <div className="text-sm font-semibold mb-1">Who receives first?</div>
              {!receivingTeam ? (
                <div className="text-xs text-base-content/50">
                  Pick the first server first.
                </div>
              ) : (
                <div className="flex gap-2">
                  {[receivingTeam?.player1, receivingTeam?.player2].map(
                    (p, i) =>
                      p && (
                        <button
                          key={p.guid ?? i}
                          className={`btn btn-sm ${
                            setupFirstReceiver?.guid === p.guid ? "btn-primary" : "btn-outline"
                          }`}
                          onClick={() => setSetupFirstReceiver(p)}
                        >
                          {playerInitialAndName({ player: p })}
                        </button>
                      ),
                  )}
                </div>
              )}
            </div>
            <button
              className="btn btn-primary btn-block"
              disabled={!setupFirstServer || !setupFirstReceiver}
              onClick={() => {
                // Each team's deuce-court player stays fixed all set, both when
                // serving (their own service games) and receiving (the other
                // team's) — established here from whoever's picked as first
                // server/receiver — everything else (per-game server rotation,
                // fixed deuce/ad receiving) derives from this automatically.
                const servingTeamIsA = servingTeam?.guid === match.teamA?.guid;
                const serverMate = getTeammate(servingTeam, setupFirstServer);
                const receiverMate = getTeammate(receivingTeam, setupFirstReceiver);
                const setTeamAFormation = servingTeamIsA
                  ? { deuce: setupFirstServer, ad: serverMate }
                  : { deuce: setupFirstReceiver, ad: receiverMate };
                const setTeamBFormation = servingTeamIsA
                  ? { deuce: setupFirstReceiver, ad: receiverMate }
                  : { deuce: setupFirstServer, ad: serverMate };
                setTeamANextServer(setTeamAFormation.deuce);
                setTeamADeuceReceiver(setTeamAFormation.deuce);
                setTeamAAdReceiver(setTeamAFormation.ad);
                setTeamBNextServer(setTeamBFormation.deuce);
                setTeamBDeuceReceiver(setTeamBFormation.deuce);
                setTeamBAdReceiver(setTeamBFormation.ad);
                setNeedsDoublesSetup(false);
                setSetupFirstServer(null);
                setSetupFirstReceiver(null);
              }}
            >
              Start Set
            </button>
          </div>
          {/* No backdrop click-to-dismiss — coding can't start until this is submitted. */}
        </div>
        );
      })()}

      {hitterPrompt && (
        <div className="modal modal-open">
          <div className="modal-box w-full max-w-xs">
            <h3 className="font-bold text-lg mb-4">{hitterPrompt.title}</h3>
            <div className="flex flex-col gap-2">
              {[hitterPrompt.team?.player1, hitterPrompt.team?.player2].map(
                (p, i) =>
                  p && (
                    <button
                      key={p.guid ?? i}
                      className={`btn ${
                        hitterPrompt.suggested?.guid === p.guid
                          ? "btn-primary"
                          : "btn-outline"
                      }`}
                      onClick={() => hitterPrompt.onPick(p)}
                    >
                      {playerInitialAndName({ player: p })}
                    </button>
                  ),
              )}
            </div>
          </div>
          {/* No backdrop click-to-dismiss — a hitter must be picked before
              the event can be created. */}
        </div>
      )}
    </>
  );
}

export default CodingPage;
